(() => {
  'use strict';

  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  const theme = window.WhiteAppleTheme || {};
  const routes = theme.routes || {};
  const strings = theme.strings || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const dialogOpeners = new WeakMap();
  const dialogMotionTimers = new WeakMap();
  const dialogMotionFrames = new WeakMap();
  const mobileMenuClosers = new WeakMap();
  let cartPointerInteraction = false;
  const setCartPointerInteraction = (isPointer) => {
    cartPointerInteraction = isPointer;
    document.querySelectorAll('[data-cart-drawer], [data-cart-tool-panel]').forEach((dialog) => {
      dialog.classList.toggle('is-pointer-open', isPointer);
    });
  };
  document.addEventListener('pointerdown', () => setCartPointerInteraction(true), { capture: true, passive: true });
  document.addEventListener('keydown', (event) => {
    if (['Tab', 'Enter', ' ', 'Escape', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      setCartPointerInteraction(false);
    }
  }, true);
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const elements = (root, selector) => {
    const matches = root instanceof Element && root.matches(selector) ? [root] : [];
    return [...matches, ...root.querySelectorAll(selector)];
  };

  const route = (name, fallback) => routes[name] || fallback;

  const focusableElements = (root) =>
    [...root.querySelectorAll(focusableSelector)].filter((element) => {
      const style = window.getComputedStyle(element);
      return !element.closest('[hidden], [inert]') && style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
    });

  const announce = (message) => {
    const region = document.querySelector('[data-live-region]');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 20);
  };

  const syncBodyLock = () => {
    const hasOpenDialog = Boolean(document.querySelector('dialog[open]'));
    const hasOpenMenu = Boolean(document.querySelector('[data-mobile-menu][open]'));
    const hasOpenFacets = Boolean(document.querySelector('[data-collection-facets].is-active'));
    document.body.classList.toggle('is-overlay-open', hasOpenDialog || hasOpenMenu || hasOpenFacets);
  };

  const initDialog = (dialog) => {
    if (dialog.dataset.runtimeDialog === 'true') return;
    dialog.dataset.runtimeDialog = 'true';

    dialog.addEventListener('keydown', (event) => {
      if (event.target.closest('dialog') !== dialog) return;
      if (event.key === 'Enter' && dialog.matches('[data-cart-tool-panel]') && event.target instanceof HTMLInputElement) {
        const submit = dialog.querySelector('[data-cart-discount-apply], [data-cart-shipping-submit]');
        if (submit) { event.preventDefault(); submit.click(); }
        return;
      }
      if (event.key !== 'Tab') return;
      dialog.classList.remove('is-pointer-open');
      const focusable = focusableElements(dialog);
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog(dialog);
    });

    dialog.addEventListener('cancel', (event) => {
      if (event.target !== dialog || !dialog.matches('[data-cart-drawer], [data-cart-tool-panel], [data-mobile-language-dialog]')) return;
      event.preventDefault();
      closeDialog(dialog);
    });

    dialog.addEventListener('close', () => {
      (dialogMotionFrames.get(dialog) || []).forEach((frame) => window.cancelAnimationFrame(frame));
      dialogMotionFrames.delete(dialog);
      if (dialog.matches('[data-cart-tool-panel], [data-mobile-language-dialog]')) {
        dialogOpeners.get(dialog)?.setAttribute('aria-expanded', 'false');
      }
      dialog.querySelectorAll('dialog[open]').forEach((panel) => panel.close());
      (dialogMotionTimers.get(dialog) || []).forEach((timer) => window.clearTimeout(timer));
      dialogMotionTimers.delete(dialog);
      dialog.classList.remove('is-active');
      dialog.removeAttribute('aria-modal');
      syncBodyLock();
      const opener = dialogOpeners.get(dialog);
      const openedByPointer = dialog.classList.contains('is-pointer-open');
      if (opener?.isConnected && !openedByPointer) opener.focus();
      else opener?.blur();
      dialogOpeners.delete(dialog);
      dialog.classList.remove('is-pointer-open');
    });
  };

  const openDialog = (dialog, opener, focusTarget) => {
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    initDialog(dialog);
    (dialogMotionTimers.get(dialog) || []).forEach((timer) => window.clearTimeout(timer));
    dialogMotionTimers.delete(dialog);
    if (opener) dialogOpeners.set(dialog, opener);
    const isSearch = dialog.matches('[data-search-dialog]');
    const isCart = dialog.matches('[data-cart-drawer]');
    const isCartTool = dialog.matches('[data-cart-tool-panel]');
    const isLanguage = dialog.matches('[data-mobile-language-dialog]');
    if (isCart || isCartTool) dialog.classList.toggle('is-pointer-open', cartPointerInteraction);
    if ((isCart || isCartTool) && dialog.open && dialog.classList.contains('is-active')) return true;
    const isMobileSearch = isSearch && window.matchMedia('(max-width: 749px)').matches;
    if (isSearch || isCart || isCartTool || isLanguage) dialog.classList.remove('is-active');
    if (!dialog.open) {
      if (isMobileSearch && typeof dialog.show === 'function') {
        dialog.show();
        dialog.setAttribute('aria-modal', 'true');
      } else if (isLanguage) {
        // Native dialogs otherwise focus the first button while the sheet is
        // below the viewport. Exclude its controls only during native opening.
        const surface = dialog.querySelector('[data-dialog-surface]');
        if (surface) surface.inert = true;
        try {
          dialog.showModal();
        } finally {
          if (surface) surface.inert = false;
        }
        dialog.focus({ preventScroll: true });
      } else {
        dialog.showModal();
      }
    }
    syncBodyLock();

    const focusInitial = () => {
      const initial =
        focusTarget ||
        dialog.querySelector('[data-dialog-initial-focus]') ||
        focusableElements(dialog)[0];
      initial?.focus({ preventScroll: true });
      if (initial instanceof HTMLInputElement && initial.type === 'search') initial.select();
    };

    if (isCart || isCartTool || isLanguage) {
      (dialogMotionFrames.get(dialog) || []).forEach((frame) => window.cancelAnimationFrame(frame));
      const surface = dialog.querySelector(isCart || isLanguage ? '[data-dialog-surface]' : '.cart-tool__surface');
      surface?.getBoundingClientRect();
      const activate = () => {
        if (!dialog.open) return;
        dialog.classList.add('is-active');
        const timer = window.setTimeout(focusInitial, reduceMotion.matches ? 0 : isLanguage ? 400 : 650);
        dialogMotionTimers.set(dialog, [timer]);
      };
      if (reduceMotion.matches) activate();
      else {
        const frames = [];
        frames.push(window.requestAnimationFrame(() => {
          frames.push(window.requestAnimationFrame(activate));
        }));
        dialogMotionFrames.set(dialog, frames);
      }
    } else if (isSearch) {
      const timers = [];
      const activationTimer = window.setTimeout(
        () => {
          if (!dialog.open) return;
          dialog.classList.add('is-active');
          if (isSearch) dialog.dispatchEvent(new CustomEvent('whiteapple:search-open'));
          timers.push(
            window.setTimeout(
              focusInitial,
              reduceMotion.matches ? 0 : isSearch ? (isMobileSearch ? 800 : 650) : 650
            )
          );
        },
        reduceMotion.matches ? 0 : isMobileSearch ? 75 : 20
      );
      timers.push(activationTimer);
      dialogMotionTimers.set(dialog, timers);
    } else {
      window.requestAnimationFrame(focusInitial);
    }
    return true;
  };

  const closeDialog = (dialog) => {
    if (!dialog?.open) return;
    const isSearch = dialog.matches('[data-search-dialog]');
    const isCart = dialog.matches('[data-cart-drawer]');
    const isCartTool = dialog.matches('[data-cart-tool-panel]');
    const isLanguage = dialog.matches('[data-mobile-language-dialog]');
    (dialogMotionFrames.get(dialog) || []).forEach((frame) => window.cancelAnimationFrame(frame));
    dialogMotionFrames.delete(dialog);
    if ((!isSearch && !isCart && !isCartTool && !isLanguage) || !dialog.classList.contains('is-active')) {
      dialog.close();
      return;
    }
    (dialogMotionTimers.get(dialog) || []).forEach((timer) => window.clearTimeout(timer));
    dialog.classList.remove('is-active');
    const closeTimer = window.setTimeout(
      () => {
        if (dialog.open) dialog.close();
      },
      reduceMotion.matches ? 0 : isLanguage ? 400 : (isCart || isCartTool) ? 600 : 800
    );
    dialogMotionTimers.set(dialog, [closeTimer]);
  };

  const initSheetDismiss = (root, surface, close, mediaQuery) => {
    const handle = surface?.querySelector('[data-sheet-drag-handle]');
    if (!handle || handle.dataset.runtimeSheetDrag === 'true') return;
    root._whiteAppleSheetCleanup?.();
    const controller = new AbortController();
    const listenerOptions = { signal: controller.signal };
    handle.dataset.runtimeSheetDrag = 'true';
    const media = window.matchMedia(mediaQuery);
    let pointerId = null;
    let startY = 0;
    let latestY = 0;
    let startTime = 0;
    let dragged = false;
    let resetTimer = 0;

    const clear = () => {
      window.clearTimeout(resetTimer);
      resetTimer = 0;
      pointerId = null;
      dragged = false;
      surface.classList.remove('is-sheet-dragging', 'is-sheet-resetting', 'is-sheet-dismissing');
      surface.style.removeProperty('--sheet-drag-y');
    };

    const finish = (event) => {
      if (pointerId === null || event.pointerId !== pointerId) return;
      const distance = Math.max(0, latestY - startY);
      const elapsed = Math.max(1, Date.now() - startTime);
      const dismiss = dragged && (distance >= 88 || distance / elapsed >= 0.55);
      pointerId = null;

      if (dismiss) {
        surface.classList.remove('is-sheet-dragging');
        surface.classList.add('is-sheet-dismissing');
        surface.style.setProperty('--sheet-drag-y', '100dvh');
        resetTimer = window.setTimeout(() => {
          close();
          clear();
        }, reduceMotion.matches ? 0 : 220);
        return;
      }

      surface.classList.remove('is-sheet-dragging');
      surface.classList.add('is-sheet-resetting');
      surface.style.setProperty('--sheet-drag-y', '0px');
      resetTimer = window.setTimeout(clear, reduceMotion.matches ? 0 : 280);
    };

    handle.addEventListener('pointerdown', (event) => {
      if (!media.matches || event.button !== 0) return;
      window.clearTimeout(resetTimer);
      pointerId = event.pointerId;
      startY = event.clientY;
      latestY = event.clientY;
      startTime = Date.now();
      dragged = false;
      surface.classList.remove('is-sheet-resetting', 'is-sheet-dismissing');
      surface.classList.add('is-sheet-dragging');
      surface.style.setProperty('--sheet-drag-y', '0px');
    }, listenerOptions);
    handle.addEventListener('click', () => {
      if (media.matches && !dragged) close();
    }, listenerOptions);

    window.addEventListener('pointermove', (event) => {
      if (pointerId === null || event.pointerId !== pointerId) return;
      latestY = event.clientY;
      const distance = Math.max(0, latestY - startY);
      if (distance > 4) dragged = true;
      if (!dragged) return;
      event.preventDefault();
      surface.style.setProperty('--sheet-drag-y', `${distance}px`);
    }, listenerOptions);
    window.addEventListener('pointerup', finish, listenerOptions);
    window.addEventListener('pointercancel', finish, listenerOptions);
    root.addEventListener('close', clear, listenerOptions);
    root.addEventListener('toggle', () => {
      if (!root.open) clear();
    }, listenerOptions);
    root._whiteAppleSheetCleanup = () => {
      clear();
      controller.abort();
      delete handle.dataset.runtimeSheetDrag;
    };
  };

  const initCollectionFacets = (form) => {
    if (form.dataset.runtimeCollectionFacets === 'true') return;
    form.dataset.runtimeCollectionFacets = 'true';

    const drawer = form.querySelector('[data-collection-facets]');
    const surface = drawer?.querySelector('[data-dialog-surface]');
    const openers = [...form.querySelectorAll('[data-collection-facets-open]')];
    const closeButtons = [...(drawer?.querySelectorAll('[data-collection-facets-close]') || [])];
    const sortRoot = form.querySelector('[data-collection-sort]');
    const sortToggle = sortRoot?.querySelector('[data-collection-sort-toggle]');
    const sortPanel = sortRoot?.querySelector('[data-collection-sort-panel]');
    const sortClose = sortRoot?.querySelector('[data-collection-sort-close]');
    const sortInput = form.querySelector('[data-collection-sort-input]');
    const sortLabel = form.querySelector('[data-collection-sort-label]');
    const drawerSort = form.querySelector('[data-collection-facet-sort]');
    const resultCount = form.querySelector('[data-facet-result-count]');
    const applyButton = form.querySelector('.collection-facets__apply');
    let activeOpener = null;
    let drawerTimer = 0;
    let countTimer = 0;
    let countRequest = null;
    let sortTimer = 0;

    const closeSort = (restoreFocus = false) => {
      if (!sortRoot || !sortPanel || !sortToggle) return;
      window.clearTimeout(sortTimer);
      sortRoot.classList.remove('is-open');
      sortPanel.inert = true;
      sortTimer = window.setTimeout(() => {
        sortPanel.hidden = true;
        sortRoot.classList.remove('opens-up');
      }, reduceMotion.matches ? 0 : 500);
      sortToggle.setAttribute('aria-expanded', 'false');
      if (restoreFocus) sortToggle.focus({ preventScroll: true });
    };

    const closeDrawer = (restoreFocus = false) => {
      if (!drawer || drawer.hidden) return;
      window.clearTimeout(drawerTimer);
      drawer.classList.remove('is-active');
      drawer.setAttribute('aria-hidden', 'true');
      openers.forEach((opener) => opener.setAttribute('aria-expanded', 'false'));
      syncBodyLock();
      drawerTimer = window.setTimeout(
        () => {
          drawer.hidden = true;
          drawer.dispatchEvent(new Event('close'));
          if (restoreFocus && activeOpener?.isConnected) activeOpener.focus({ preventScroll: true });
          else activeOpener?.blur();
          activeOpener = null;
        },
        reduceMotion.matches ? 0 : 620
      );
    };

    const openDrawer = (opener, openedByKeyboard) => {
      if (!drawer || !surface) return;
      window.clearTimeout(drawerTimer);
      closeSort();
      activeOpener = opener;
      drawer.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      openers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          drawer.classList.add('is-active');
          syncBodyLock();
          if (openedByKeyboard) {
            const initial = focusableElements(drawer).find((element) =>
              element.tabIndex !== -1 && !element.matches('[data-sheet-drag-handle]')
            );
            initial?.focus({ preventScroll: true });
          }
        });
      });
    };

    if (drawer && surface) {
      initSheetDismiss(drawer, surface, () => closeDrawer(false), '(max-width: 1099px)');
      openers.forEach((opener) => {
        opener.addEventListener('click', (event) => openDrawer(opener, event.detail === 0));
      });
      // Keep the fixed control outside section paint layers, including the footer.
      const floatingOpener = openers.find((opener) => opener.classList.contains('collection-facets-trigger--mobile'));
      const collectionSection = form.closest('.shopify-section');
      if (floatingOpener && collectionSection) {
        // Show the floating control over results, after the collection banner.
        const results = collectionSection.querySelector('[data-product-grid], [data-search-results], .empty-state') || form;
        floatingOpener.classList.add('is-outside-collection');
        document.body.append(floatingOpener);
        const visibilityObserver = new IntersectionObserver(([entry]) => {
          floatingOpener.classList.toggle('is-outside-collection', !entry.isIntersecting);
        });
        visibilityObserver.observe(results);
        collectionSection.addEventListener('shopify:section:unload', () => {
          visibilityObserver.disconnect();
          floatingOpener.remove();
        }, { once: true });
      }
      closeButtons.forEach((button) => {
        button.addEventListener('click', () => closeDrawer(button !== drawer.querySelector('.collection-facets__backdrop')));
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && drawer.classList.contains('is-active') && !drawer.contains(event.target)) {
          event.preventDefault();
          closeDrawer(true);
        }
      });
      drawer.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeDrawer(true);
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = focusableElements(drawer).filter((element) => element.tabIndex !== -1);
        if (!focusable.length) {
          event.preventDefault();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }

    const updatePrice = (priceRoot, source) => {
      const minRange = priceRoot.querySelector('[data-facet-price-min-range]');
      const maxRange = priceRoot.querySelector('[data-facet-price-max-range]');
      const minField = priceRoot.querySelector('[data-facet-price-min]');
      const maxField = priceRoot.querySelector('[data-facet-price-max]');
      const track = priceRoot.querySelector('[data-facet-price-range]');
      if (!minRange || !maxRange || !minField || !maxField || !track) return;

      const maximum = Number(maxRange.max) || 1;
      let minimumValue = Number(minField.value || 0);
      let maximumValue = Number(maxField.value || maximum);

      if (source === minRange) minimumValue = Math.min(Number(minRange.value), maximumValue);
      if (source === maxRange) maximumValue = Math.max(Number(maxRange.value), minimumValue);
      minimumValue = Math.max(0, Math.min(minimumValue, maximum));
      maximumValue = Math.max(minimumValue, Math.min(maximumValue, maximum));

      minRange.value = String(minimumValue);
      maxRange.value = String(maximumValue);
      if (source === minRange) minField.value = minimumValue > 0 ? String(minimumValue) : '';
      if (source === maxRange) maxField.value = maximumValue < maximum ? String(maximumValue) : '';
      minField.max = String(Math.max(0, Math.min(Number(maxField.value || maximum), maximum)));
      maxField.min = String(Math.max(0, Math.min(Number(minField.value || 0), maximum)));
      track.style.setProperty('--range-start', `${(minimumValue / maximum) * 100}%`);
      track.style.setProperty('--range-end', `${(maximumValue / maximum) * 100}%`);
    };

    form.querySelectorAll('[data-facet-price]').forEach((priceRoot) => {
      updatePrice(priceRoot);
      priceRoot.querySelectorAll('input').forEach((input) => {
        input.addEventListener('input', () => {
          updatePrice(priceRoot, input);
          refreshResultCount();
        });
      });
    });

    const refreshResultCount = () => {
      if (!resultCount || !drawer || drawer.hidden) return;
      window.clearTimeout(countTimer);
      const invalidPrice = [...form.querySelectorAll('[data-facet-price] input[type="number"]')].some(input => !input.validity.valid);
      if (invalidPrice) {
        countRequest?.abort();
        return;
      }
      countTimer = window.setTimeout(async () => {
        countRequest?.abort();
        countRequest = new AbortController();
        const params = new URLSearchParams();
        new FormData(form).forEach((value, key) => {
          if (String(value).trim() !== '') params.append(key, String(value));
        });
        const currentParams = new URLSearchParams(window.location.search);
        ['preview_theme_id', '_fd', 'pb'].forEach((key) => {
          if (currentParams.has(key)) params.set(key, currentParams.get(key));
        });
        const url = `${form.action}${params.size ? `?${params.toString()}` : ''}`;
        applyButton?.classList.add('is-loading');
        applyButton?.setAttribute('aria-busy', 'true');
        try {
          const response = await fetch(url, {
            headers: { Accept: 'text/html' },
            signal: countRequest.signal
          });
          if (!response.ok) return;
          const html = await response.text();
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const nextCount = documentFragment.querySelector('[data-facet-result-count]')?.textContent?.trim();
          if (nextCount) resultCount.textContent = nextCount;
        } catch (error) {
          if (error.name !== 'AbortError') console.warn('Unable to update collection result count.', error);
        } finally {
          applyButton?.classList.remove('is-loading');
          applyButton?.removeAttribute('aria-busy');
        }
      }, 220);
    };

    drawerSort?.addEventListener('change', () => {
      if (sortInput) sortInput.value = drawerSort.value;
      refreshResultCount();
    });

    form.addEventListener('change', (event) => {
      if (event.target === drawerSort) return;
      if (event.target.matches('[data-color-label]')) {
        const group = event.target.closest('[data-filter-group]');
        const selected = Array.from(group.querySelectorAll('[data-color-label]:checked'), (input) => input.dataset.colorLabel);
        const summary = group.querySelector('[data-selected-colors]');
        if (summary) {
          summary.hidden = selected.length === 0;
          summary.querySelector('[data-selected-color-names]').textContent = selected.join(', ');
        }
      }
      if (event.target.closest('[data-collection-facets]')) refreshResultCount();
    });
    form.addEventListener('submit', () => {
      form.querySelectorAll('input[name]').forEach((input) => {
        if (input.type !== 'hidden' && input.value.trim() === '') input.disabled = true;
      });
    });

    if (sortRoot && sortToggle && sortPanel) {
      sortToggle.addEventListener('click', () => {
        const opening = !sortRoot.classList.contains('is-open');
        if (!opening) {
          closeSort();
          return;
        }
        window.clearTimeout(sortTimer);
        const triggerRect = sortToggle.getBoundingClientRect();
        const panelHeight = 108 + sortPanel.querySelectorAll('[data-collection-sort-option]').length * 40 - 8;
        const roomBelow = window.innerHeight - triggerRect.top - 16;
        const headerBottom = document.querySelector('[data-site-header]')?.getBoundingClientRect().bottom || 0;
        const roomAbove = triggerRect.bottom - Math.max(0, headerBottom) - 16;
        const opensUp = roomBelow < panelHeight && roomAbove >= panelHeight;
        sortRoot.classList.toggle('opens-up', opensUp);
        sortRoot.style.setProperty('--sort-trigger-width', `${triggerRect.width}px`);
        sortRoot.style.setProperty('--sort-panel-height', `${panelHeight}px`);
        sortPanel.hidden = false;
        sortPanel.inert = false;
        sortToggle.setAttribute('aria-expanded', 'true');
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
          if (sortToggle.getAttribute('aria-expanded') === 'true') sortRoot.classList.add('is-open');
        }));
      });
      sortClose?.addEventListener('click', () => closeSort(true));
      sortPanel.querySelectorAll('[data-collection-sort-option]').forEach((option) => {
        option.addEventListener('click', () => {
          const value = option.dataset.value || '';
          if (sortInput) sortInput.value = value;
          if (drawerSort) drawerSort.value = value;
          if (sortLabel) sortLabel.textContent = option.textContent.trim();
          sortPanel.querySelectorAll('[data-collection-sort-option]').forEach((item) => {
            const selected = item === option;
            item.classList.toggle('is-selected', selected);
            item.setAttribute('aria-selected', String(selected));
          });
          closeSort();
          if (form.requestSubmit) form.requestSubmit();
          else form.submit();
        });
      });
      document.addEventListener('click', (event) => {
        if (!sortRoot.contains(event.target)) closeSort();
      });
      sortRoot.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !sortPanel.hidden) {
          event.preventDefault();
          closeSort(true);
        }
      });
    }
  };

  class AnnouncementRotator {
    constructor(root) {
      this.root = root;
      this.messages = [...root.querySelectorAll('[data-announcement-message]')];
      this.button = root.querySelector('[data-announcement-toggle]');
      this.label = root.querySelector('[data-announcement-toggle-label]');
      this.previousButton = root.querySelector('[data-announcement-previous]');
      this.nextButton = root.querySelector('[data-announcement-next]');
      this.status = root.querySelector('[data-announcement-status]');
      this.languageDisclosure = root.querySelector('.announcement-bar__language-disclosure');
      this.index = Math.max(0, this.messages.findIndex((message) => message.classList.contains('is-active')));
      this.timer = null;
      this.userPaused = false;
      this.temporarilyPaused = false;
      this.interval = Math.max(3000, Number(root.dataset.interval) || 5000);
      this.autoplay = root.dataset.autoplay === 'true';
      this.pauseLabel = this.button?.dataset.pauseLabel || 'Pause';
      this.playLabel = this.button?.dataset.playLabel || 'Play';

      this.button?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.updateButton();
        this.schedule();
      });
      this.previousButton?.addEventListener('click', () => this.navigate(-1));
      this.nextButton?.addEventListener('click', () => this.navigate(1));
      this.languageDisclosure?.querySelector('summary')?.addEventListener(
        'click',
        (event) => {
          if (
            event.detail > 0 &&
            this.languageDisclosure.open &&
            window.matchMedia('(hover: hover) and (pointer: fine)').matches
          ) {
            event.preventDefault();
          }
        },
        { signal: this.signal }
      );
      this.languageDisclosure?.addEventListener(
        'pointerenter',
        (event) => {
          if (event.pointerType === 'touch') return;
          this.languageDisclosure.open = true;
        },
        { signal: this.signal }
      );
      this.languageDisclosure?.addEventListener(
        'pointerleave',
        (event) => {
          if (event.pointerType === 'touch') return;
          this.languageDisclosure.open = false;
        },
        { signal: this.signal }
      );
      root.addEventListener('pointerenter', () => {
        this.temporarilyPaused = true;
        this.schedule();
      });
      root.addEventListener('pointerleave', () => {
        this.temporarilyPaused = false;
        this.schedule();
      });
      root.addEventListener('focusin', () => {
        this.temporarilyPaused = true;
        this.schedule();
      });
      root.addEventListener('focusout', (event) => {
        if (root.contains(event.relatedTarget)) return;
        this.temporarilyPaused = false;
        this.schedule();
      });
      document.addEventListener('visibilitychange', () => this.schedule(), { signal: this.signal });
      document.addEventListener(
        'pointerdown',
        (event) => {
          if (!this.languageDisclosure?.open || this.languageDisclosure.contains(event.target)) return;
          this.languageDisclosure.open = false;
        },
        { signal: this.signal }
      );
      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Escape' || !this.languageDisclosure?.open) return;
          this.languageDisclosure.open = false;
          this.languageDisclosure.querySelector('summary')?.focus();
        },
        { signal: this.signal }
      );
      reduceMotion.addEventListener?.('change', () => this.schedule(), { signal: this.signal });
      this.show(this.index);
      this.updateButton();
      this.schedule();
    }

    navigate(direction) {
      this.show(this.index + direction);
      const template = this.status?.dataset.statusTemplate || '';
      if (this.status && template) {
        this.status.textContent = template
          .replace('[current]', String(this.index + 1))
          .replace('[total]', String(this.messages.length));
      }
      this.schedule();
    }

    get signal() {
      if (!this.controller) this.controller = new AbortController();
      return this.controller.signal;
    }

    show(index) {
      if (!this.messages.length) return;
      this.index = (index + this.messages.length) % this.messages.length;
      this.messages.forEach((message, messageIndex) => {
        const active = messageIndex === this.index;
        message.classList.toggle('is-active', active);
        message.setAttribute('aria-hidden', String(!active));
        message.toggleAttribute('inert', !active);
      });
    }

    updateButton() {
      if (!this.button) return;
      const actionLabel = this.userPaused ? this.playLabel : this.pauseLabel;
      this.button.setAttribute('aria-pressed', String(this.userPaused));
      this.button.setAttribute('aria-label', actionLabel);
      if (this.label) this.label.textContent = actionLabel;
    }

    schedule() {
      window.clearInterval(this.timer);
      this.timer = null;
      if (
        !this.autoplay ||
        this.messages.length < 2 ||
        this.userPaused ||
        this.temporarilyPaused ||
        document.hidden ||
        reduceMotion.matches
      ) {
        return;
      }
      this.timer = window.setInterval(() => this.show(this.index + 1), this.interval);
    }

    destroy() {
      window.clearInterval(this.timer);
      this.controller?.abort();
    }
  }

  class SearchTyped {
    constructor(root, input) {
      this.root = root;
      this.input = input;
      this.text = root?.querySelector('[data-search-typed-text]');
      this.cursor = root?.querySelector('[data-search-typed-cursor]');
      this.animation = null;
      this.initialized = false;
      if (!this.root || !this.input || !this.text || !this.cursor) return;
      this.input.addEventListener('pointerdown', () => this.hide());
      this.input.addEventListener('input', () => this.hide());
    }

    hide() {
      this.root.hidden = true;
      this.animation?.cancel();
      this.animation = null;
    }

    wait(duration) {
      return new Promise((resolve) => window.setTimeout(resolve, duration));
    }

    async animateWidth(from, to, duration) {
      if (this.root.hidden) return false;
      if (!this.text.animate) {
        this.text.style.width = `${to}px`;
        return true;
      }
      this.animation = this.text.animate([{ width: `${from}px` }, { width: `${to}px` }], {
        duration,
        easing: 'ease',
        fill: 'forwards'
      });
      try {
        await this.animation.finished;
      } catch {
        return false;
      }
      this.text.style.width = `${to}px`;
      this.animation.cancel();
      this.animation = null;
      return !this.root.hidden;
    }

    async type(value, duration) {
      this.text.textContent = value;
      this.text.style.width = 'auto';
      const width = this.text.scrollWidth;
      this.text.style.width = '0px';
      await new Promise((resolve) => window.requestAnimationFrame(resolve));
      return this.animateWidth(0, width, duration);
    }

    async play() {
      if (!this.root || !this.text || !this.cursor || this.initialized || this.root.hidden) return;
      this.initialized = true;
      const firstText = this.root.dataset.firstText || '';
      const lastText = this.root.dataset.lastText || '';
      if (reduceMotion.matches) {
        this.text.textContent = lastText;
        this.text.style.width = 'auto';
        this.cursor.classList.add('is-blinking');
        return;
      }
      await this.wait(500);
      if (!(await this.type(firstText, 1000))) return;
      this.cursor.classList.add('is-blinking');
      await this.wait(600);
      this.cursor.classList.remove('is-blinking');
      const currentWidth = this.text.getBoundingClientRect().width;
      if (!(await this.animateWidth(currentWidth, 0, 250))) return;
      if (!(await this.type(lastText, 1000))) return;
      this.cursor.classList.add('is-blinking');
    }
  }

  // Require every keyword in product identity fields, excluding incidental
  // mentions of competing products in descriptions and merchandising tags.
  const buildKeywordQuery = (value) => {
    const words = value.replace(/["\\]/g, ' ').trim().split(/\s+/).filter(Boolean);
    const fields = ['title', 'vendor', 'product_type', 'variants.title', 'variants.sku'];
    return words.map((word) => `(${fields.map((field) => `${field}:"${word}"`).join(' OR ')})`).join(' AND ');
  };

  class PredictiveSearch {
    constructor(root) {
      this.root = root;
      this.input = root.querySelector('[data-predictive-search-input]');
      this.results = root.querySelector('[data-predictive-search-results]');
      this.clearButton = root.querySelector('[data-predictive-search-clear]');
      this.initialMarkup = this.results?.innerHTML || '';
      this.timer = null;
      this.request = null;
      if (!this.input || !this.results) return;
      this.typed = new SearchTyped(root.querySelector('[data-search-typed]'), this.input);
      this.input.addEventListener('input', () => this.schedule());
      this.clearButton?.addEventListener('click', () => {
        this.input.value = '';
        this.schedule();
        this.input.focus();
      });
      root.addEventListener('whiteapple:search-open', () => this.typed.play());
      root.addEventListener('close', () => this.abort());
    }

    schedule() {
      window.clearTimeout(this.timer);
      this.abort();
      const term = this.input.value.trim();
      if (this.clearButton) this.clearButton.hidden = !term;
      this.input.setAttribute('aria-expanded', String(Boolean(term)));
      if (!term) {
        this.results.innerHTML = this.initialMarkup;
        this.results.setAttribute('aria-busy', 'false');
        return;
      }
      this.timer = window.setTimeout(() => this.search(term), 250);
    }

    abort() {
      this.request?.abort();
      this.request = null;
    }

    async search(term) {
      this.request = new AbortController();
      this.results.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(this.root.dataset.predictiveSearchUrl || route('predictiveSearch', '/search/suggest'), location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('resources[type]', 'product,collection');
        url.searchParams.set(
          'resources[limit]',
          window.matchMedia('(max-width: 749px)').matches ? '3' : this.root.dataset.predictiveSearchLimit || '6'
        );
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        url.searchParams.set('resources[options][fields]', 'title,vendor,product_type,variants.title,variants.sku');
        url.searchParams.set('section_id', this.root.dataset.predictiveSearchSection || 'predictive-search');
        const response = await fetch(url, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: this.request.signal
        });
        if (!response.ok) throw new Error(String(response.status));
        const markup = await response.text();
        if (this.input.value.trim() !== term) return;
        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const nextResults = parsed.querySelector('[data-predictive-search-results]');
        if (!nextResults) throw new Error('results');
        this.results.replaceChildren(...nextResults.childNodes);
        const viewAll = this.results.querySelector('.predictive-search__view-all');
        if (viewAll) {
          const resultsUrl = new URL(viewAll.href);
          resultsUrl.searchParams.set('q', buildKeywordQuery(term));
          viewAll.href = resultsUrl.href;
        }
        this.input.setAttribute('aria-expanded', 'true');
      } catch (error) {
        if (error.name !== 'AbortError') this.results.innerHTML = this.initialMarkup;
      } finally {
        if (this.input.value.trim() === term) this.results.setAttribute('aria-busy', 'false');
      }
    }
  }

  class CartManager {
    constructor() {
      this.queue = Promise.resolve();
    }

    drawer() {
      return document.querySelector('[data-cart-drawer]');
    }

    sectionName() {
      return this.drawer()?.dataset.cartDrawerSection || 'cart-drawer';
    }

    setStatus(message) {
      const status = this.drawer()?.querySelector('[data-cart-status]');
      if (status) status.textContent = message || '';
      if (message) announce(message);
    }

    setError(message) {
      const error = this.drawer()?.querySelector('[data-cart-error]');
      if (!error) return;
      error.textContent = message || '';
      error.hidden = !message;
    }

    setBusy(busy) {
      this.drawer()?.classList.toggle('is-loading', busy);
      this.drawer()?.setAttribute('aria-busy', String(busy));
    }

    updateCount(count) {
      const safeCount = Math.max(0, Number(count) || 0);
      document.querySelectorAll('[data-cart-count]').forEach((element) => {
        element.textContent = String(safeCount);
        element.toggleAttribute('hidden', safeCount === 0);
      });
    }

    open(opener, focusTarget) {
      const drawer = this.drawer();
      return openDialog(drawer, opener, focusTarget);
    }

    replaceDrawerHTML(markup, shouldOpen = false, focusLine = null, opener = null) {
      if (!markup) return null;
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const replacement = parsed.querySelector('[data-cart-drawer]');
      if (!replacement) return null;
      const current = this.drawer();
      const wasOpen = Boolean(current?.open);
      const previousOpener = opener || (current ? dialogOpeners.get(current) : null);
      if (current && wasOpen) {
        const scrollTop = current.querySelector('.cart-drawer__scroll')?.scrollTop || 0;
        const surface = current.querySelector('[data-dialog-surface]');
        const nextSurface = replacement.querySelector('[data-dialog-surface]');
        current.querySelectorAll('dialog[open]').forEach((panel) => panel.close());
        surface.replaceChildren(...nextSurface.childNodes);
        current.dataset.cartItemCount = replacement.dataset.cartItemCount;
        delete current.dataset.runtimeCartDrawer;
        initTree(current);
        const scroll = current.querySelector('.cart-drawer__scroll');
        if (scroll) scroll.scrollTop = scrollTop;
        this.updateCount(current.dataset.cartItemCount);
        const focusTarget = focusLine
          ? current.querySelector(`[data-cart-quantity][data-line="${Number(focusLine)}"]`)
          : current.querySelector('[data-cart-tab="cart"]');
        focusTarget?.focus({ preventScroll: true });
        return current;
      }
      if (current) {
        current._whiteAppleSheetCleanup?.();
        current._cartRecommendationResize?.disconnect();
        current.replaceWith(replacement);
      } else document.body.append(replacement);
      initTree(replacement);
      this.updateCount(replacement.dataset.cartItemCount);
      if (shouldOpen) this.open(previousOpener, replacement.querySelector('[data-cart-tab="cart"]'));
      return replacement;
    }

    async refresh(shouldOpen = false, focusLine = null, opener = null) {
      const url = new URL(route('cart', '/cart'), location.origin);
      url.searchParams.set('section_id', this.sectionName());
      const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error(strings.cartError || 'Unable to update cart.');
      const markup = await response.text();
      return this.replaceDrawerHTML(markup, shouldOpen, focusLine, opener);
    }

    async applyResponse(response, shouldOpen = true, focusLine = null, opener = null) {
      if (typeof response?.item_count !== 'undefined') this.updateCount(response.item_count);
      const sectionMarkup = response?.sections?.[this.sectionName()];
      if (sectionMarkup && this.replaceDrawerHTML(sectionMarkup, shouldOpen, focusLine, opener)) return;
      await this.refresh(shouldOpen, focusLine, opener);
    }

    enqueue(task) {
      const job = this.queue.catch(() => undefined).then(task);
      this.queue = job;
      return job;
    }

    mutate(path, payload, focusLine = null) {
      return this.enqueue(async () => {
        this.setBusy(true);
        this.setError('');
        this.setStatus(strings.loading || 'Loading');
        try {
          const response = await fetch(path, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              ...payload,
              sections: [this.sectionName()],
              sections_url: `${location.pathname}${location.search}`
            })
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || data.message || strings.cartError || 'Unable to update cart.');
          await this.applyResponse(data, true, focusLine);
          this.setStatus(strings.cartUpdated || 'Cart updated');
          return data;
        } catch (error) {
          this.setError(error.message || strings.cartError || 'Unable to update cart.');
          throw error;
        } finally {
          this.setBusy(false);
        }
      });
    }

    change(line, quantity) {
      return this.mutate(route('cartChange', '/cart/change.js'), { line: Number(line), quantity: Number(quantity) }, line);
    }

    updateNote(note) {
      return this.mutate(route('cartUpdate', '/cart/update.js'), { note });
    }

    applyDiscount(code) {
      return this.enqueue(async () => {
        this.setBusy(true);
        this.setError('');
        try {
          const updateUrl = `${route('root', '/')}cart/update.js`;
          const response = await fetch(updateUrl, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              discount: code,
              sections: [this.sectionName()],
              sections_url: `${location.pathname}${location.search}`
            })
          });
          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.description || data.message || strings.cartError || 'Unable to apply discount.');
          }

          const applications = [
            ...(Array.isArray(data.cart_level_discount_applications)
              ? data.cart_level_discount_applications
              : []),
            ...(Array.isArray(data.discount_applications) ? data.discount_applications : []),
            ...(Array.isArray(data.items)
              ? data.items.flatMap((item) =>
                  Array.isArray(item.discount_allocations)
                    ? item.discount_allocations.map((allocation) => allocation.discount_application)
                    : []
                )
              : [])
          ].filter(Boolean);
          const normalizedCode = code.toLocaleLowerCase();
          const applied = applications.some((application) =>
            [application.code, application.title].some(
              (value) => String(value || '').toLocaleLowerCase() === normalizedCode
            )
          );

          if (applied) await this.applyResponse(data, true);
          return { applied, data };
        } finally {
          this.setBusy(false);
        }
      });
    }

    submit(form) {
      return this.enqueue(async () => {
        this.setBusy(true);
        this.setError('');
        this.setStatus(strings.loading || 'Loading');
        try {
          const data = new FormData(form);
          data.set('sections', this.sectionName());
          data.set('sections_url', `${location.pathname}${location.search}`);
          const response = await fetch(route('cartUpdate', '/cart/update.js'), {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: data
          });
          const result = await response.json();
          if (!response.ok) {
            throw new Error(result.description || result.message || strings.cartError || 'Unable to update cart.');
          }
          await this.applyResponse(result, true);
          this.setStatus(strings.cartUpdated || 'Cart updated');
        } catch (error) {
          this.setError(error.message || strings.cartError || 'Unable to update cart.');
          throw error;
        } finally {
          this.setBusy(false);
        }
      });
    }
  }

  const cart = new CartManager();
  window.WhiteAppleCart = cart;

  class ProductForm extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeProductForm === 'true') return;
      this.dataset.runtimeProductForm = 'true';
      this.form = this.querySelector('form');
      this.form?.addEventListener('submit', (event) => this.submit(event));
    }

    async submit(event) {
      if (!this.form || this.busy || !window.fetch || !window.FormData) return;
      event.preventDefault();
      this.busy = true;
      const button = event.submitter || this.form.querySelector('[type="submit"]');
      const status =
        this.querySelector('[data-product-status]') ||
        this.querySelector('[data-quick-add-status]') ||
        this.form.querySelector('[role="status"]');
      const originalText = button?.textContent;
      button?.setAttribute('disabled', '');
      button?.setAttribute('aria-disabled', 'true');
      button?.classList.add('is-loading');
      if (status) status.textContent = strings.loading || 'Loading';

      try {
        const data = new FormData(this.form);
        data.set('sections', cart.sectionName());
        data.set('sections_url', `${location.pathname}${location.search}`);
        const response = await fetch(route('cartAdd', '/cart/add.js'), {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: data
        });
        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.description || result.message || strings.cartError || 'Unable to add this item.');
        }
        button?.classList.add('is-success');
        const successMessage = strings.addedToCart || 'Added to cart';
        if (status) status.textContent = successMessage;
        announce(successMessage);
        await cart.applyResponse(result, true, null, button);
      } catch (error) {
        if (status) status.textContent = error.message || strings.cartError || 'Unable to add this item.';
        announce(error.message || strings.cartError || 'Unable to add this item.');
      } finally {
        this.busy = false;
        button?.classList.remove('is-loading');
        window.setTimeout(() => button?.classList.remove('is-success'), 1200);
        const section = this.closest('[data-product-section]');
        const selected = section?.querySelector('[data-variant-select]')?.selectedOptions?.[0];
        const available = selected ? selected.dataset.available === 'true' : true;
        if (available) {
          button?.removeAttribute('disabled');
          button?.removeAttribute('aria-disabled');
        }
        if (button && originalText && !button.textContent.trim()) button.textContent = originalText;
      }
    }
  }

  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  const setQuantity = (input, direction) => {
    if (!input) return;
    const step = Math.max(Number(input.step) || 1, 0.000001);
    const minimum = input.min === '' ? 0 : Number(input.min);
    const maximum = input.max === '' ? Infinity : Number(input.max);
    const current = Number(input.value);
    const baseline = Number.isFinite(current) ? current : minimum;
    const next = Math.min(maximum, Math.max(minimum, baseline + direction * step));
    const decimals = String(step).includes('.') ? String(step).split('.')[1].length : 0;
    input.value = next.toFixed(decimals);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const updateProductGalleryCounter = (section, media) => {
    const current = section.querySelector('[data-gallery-current]');
    const position = media?.dataset.mediaPosition;
    if (!current || !position || current.textContent === position) return;
    current.textContent = position;
    const label = section.querySelector('[data-gallery-position-label]');
    if (label) label.textContent = media.querySelector('figcaption')?.textContent.trim() || '';
  };

  const setActiveProductMedia = (section, mediaId, shouldScroll = false) => {
    if (!mediaId) return;
    const media = [...section.querySelectorAll('[data-product-media]')];
    const target = media.find((item) => String(item.dataset.mediaId) === String(mediaId));
    if (!target) return;
    const viewport = shouldScroll ? target.closest('.product-gallery__viewport') : null;
    const targetLeft = viewport ? target.offsetLeft - media[0].offsetLeft : 0;
    updateProductGalleryCounter(section, target);
    media.forEach((item) => {
      const active = item === target;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-hidden', String(!active));
      if (!active) item.querySelectorAll('video').forEach((video) => video.pause());
    });
    section.querySelectorAll('[data-media-thumbnail]').forEach((thumbnail) => {
      const controlled = thumbnail.getAttribute('aria-controls') || thumbnail.dataset.mediaTarget || '';
      const active = controlled === target.id;
      thumbnail.classList.toggle('is-active', active);
      thumbnail.setAttribute('aria-current', String(active));
    });
    if (shouldScroll) {
      if (viewport) {
        viewport.scrollTo({
          left: targetLeft,
          behavior: reduceMotion.matches ? 'auto' : 'smooth'
        });
      } else {
        target.scrollIntoView({
          behavior: reduceMotion.matches ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }
  };

  class ProductRuntime {
    constructor(section) {
      this.section = section;
      this.select = section.querySelector('[data-variant-select]');
      this.variants = [];
      try {
        this.variants = JSON.parse(section.querySelector('[data-product-variants]')?.textContent || '[]');
      } catch {
        this.variants = [];
      }
      this.select?.addEventListener('change', () => this.updateVariant());
      const optionButtons = section.querySelectorAll('[data-product-option-value]');
      optionButtons.forEach((button) => {
        button.addEventListener('click', () => this.selectOption(button));
      });
      if (this.select && this.variants.length > 0 && optionButtons.length > 0) {
        section.dataset.productOptionsReady = 'true';
      }
      section.querySelectorAll('[data-media-thumbnail]').forEach((thumbnail) => {
        thumbnail.addEventListener('click', () => {
          const targetId = thumbnail.dataset.mediaTarget;
          const media = targetId ? section.querySelector(`#${targetId}`) : null;
          setActiveProductMedia(section, media?.dataset.mediaId, true);
        });
      });
      section.querySelectorAll('[data-product-zoom]').forEach((trigger) => {
        trigger.addEventListener('click', (event) => this.openZoom(trigger, event));
      });
      section.querySelectorAll('.product-gallery__image > img').forEach((image) => {
        image.addEventListener('click', (event) => {
          const trigger = image.parentElement?.querySelector('[data-product-zoom]');
          if (trigger) this.openZoom(trigger, event);
        });
      });
      this.initGalleryCarousel();
      this.syncOptionPickers(this.selectedVariant());
      this.initZoomViewer();
      this.initSticky();
    }

    selectedVariant() {
      const id = String(this.select?.value || this.section.querySelector('[data-variant-input]')?.value || '');
      return this.variants.find((variant) => String(variant.id) === id);
    }

    selectOption(button) {
      if (!this.select || !button) return;
      const optionIndex = Number(button.dataset.optionIndex);
      const optionValue = button.dataset.optionValue || '';
      if (!Number.isInteger(optionIndex) || !optionValue) return;

      const currentVariant = this.selectedVariant();
      const selectedOptions = Array.isArray(currentVariant?.options)
        ? [...currentVariant.options]
        : [];
      this.section.querySelectorAll('[data-product-option-group]').forEach((group) => {
        const groupIndex = Number(group.dataset.optionIndex);
        if (!Number.isInteger(groupIndex) || selectedOptions[groupIndex]) return;
        selectedOptions[groupIndex] =
          group.querySelector('[data-product-option-value].is-selected')?.dataset.optionValue || '';
      });
      selectedOptions[optionIndex] = optionValue;

      let nextVariant = this.variants.find(
        (variant) =>
          Array.isArray(variant.options) &&
          variant.options.every((value, index) => String(value) === String(selectedOptions[index] || ''))
      );
      if (!nextVariant) {
        nextVariant =
          this.variants.find(
            (variant) =>
              variant.available &&
              Array.isArray(variant.options) &&
              String(variant.options[optionIndex]) === optionValue
          ) ||
          this.variants.find(
            (variant) =>
              Array.isArray(variant.options) &&
              String(variant.options[optionIndex]) === optionValue
          );
      }
      if (!nextVariant) return;

      this.select.value = String(nextVariant.id);
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    syncOptionPickers(variant) {
      if (!variant || !Array.isArray(variant.options)) return;
      this.section.querySelectorAll('[data-product-option-group]').forEach((group) => {
        const optionIndex = Number(group.dataset.optionIndex);
        if (!Number.isInteger(optionIndex)) return;
        const selectedValue = String(variant.options[optionIndex] || '');
        const selectedLabel = group.querySelector('[data-selected-option-value]');
        if (selectedLabel) selectedLabel.textContent = selectedValue;

        group.querySelectorAll('[data-product-option-value]').forEach((button) => {
          const value = button.dataset.optionValue || '';
          const selected = value === selectedValue;
          const matchingVariants = this.variants.filter(
            (candidate) =>
              Array.isArray(candidate.options) &&
              candidate.options.every(
                (candidateValue, index) =>
                  index === optionIndex ||
                  String(candidateValue) === String(variant.options[index] || '')
              ) &&
              String(candidate.options[optionIndex]) === value
          );
          const exists = matchingVariants.length > 0;
          const available = matchingVariants.some((candidate) => candidate.available);
          button.classList.toggle('is-selected', selected);
          button.classList.toggle('is-unavailable', exists && !available);
          button.disabled = !exists;
          button.setAttribute('aria-pressed', String(selected));
          button.setAttribute('aria-disabled', String(!exists));
        });
      });
    }

    updatePrice(option, variant) {
      const currentPrice = option?.dataset.price || '';
      const comparePrice = option?.dataset.comparePrice || '';
      const onSale = Boolean(
        variant &&
          Number(variant.compare_at_price) > Number(variant.price)
      );
      this.section.querySelectorAll('[data-price-root]').forEach((priceRoot) => {
        const current = priceRoot.querySelector('[data-price-current]');
        let compare = priceRoot.querySelector('[data-price-compare]');
        if (current && currentPrice) current.textContent = currentPrice;
        if (onSale && comparePrice) {
          if (!compare && current) {
            compare = document.createElement('s');
            compare.className = 'price__compare';
            compare.dataset.priceCompare = '';
            current.before(compare);
          }
          if (compare) {
            compare.textContent = comparePrice;
            compare.hidden = false;
          }
        } else if (compare) {
          compare.hidden = true;
        }
        priceRoot.classList.toggle('price--sale', onSale);
        const saving = priceRoot.querySelector('[data-price-saving]');
        if (saving) {
          if (onSale) {
            const percent = Math.round(
              ((Number(variant.compare_at_price) - Number(variant.price)) * 100) /
                Number(variant.compare_at_price)
            );
            const template = saving.dataset.priceSavingTemplate || '__PERCENT__% Off';
            saving.textContent = template.replace('__PERCENT__', String(percent));
            saving.hidden = false;
          } else {
            saving.hidden = true;
          }
        }
      });
    }

    updateInventory(option, variant, available) {
      const inventory = this.section.querySelector('[data-product-inventory]');
      if (!inventory) return;
      const quantity = Number(option?.dataset.inventoryQuantity ?? variant?.inventory_quantity ?? 0);
      const policy = option?.dataset.inventoryPolicy || variant?.inventory_policy || '';
      let text = strings.soldOut || 'Sold out';
      let modifier = 'out';
      const threshold = Math.max(1, Number(this.section.dataset.lowStockThreshold) || 5);
      if (available && quantity > 0) {
        text =
          quantity <= threshold
            ? (strings.lowStock || 'Only __COUNT__ left').replace('__COUNT__', String(quantity))
            : strings.inStock || 'In stock';
        modifier = quantity <= threshold ? 'low' : 'in';
      } else if (available && policy === 'continue') {
        text = strings.availableToOrder || 'Available to order';
        modifier = 'continue';
      } else if (available) {
        text = strings.inStock || 'In stock';
        modifier = 'in';
      }
      const status = document.createElement('span');
      status.className = `stock-status stock-status--${modifier}`;
      status.textContent = text;
      inventory.replaceChildren(status);
      inventory.dataset.inventoryQuantity = String(quantity);
    }

    updateVariant() {
      const option = this.select?.selectedOptions?.[0];
      if (!option) return;
      const variant = this.selectedVariant();
      const available = option.dataset.available === 'true' && variant?.available !== false;
      const featuredMediaId =
        option.dataset.featuredMediaId ||
        variant?.featured_media?.id ||
        variant?.featured_image?.id;
      const activeMedia = this.section.querySelector('[data-product-media].is-active');
      if (featuredMediaId && String(activeMedia?.dataset.mediaId) !== String(featuredMediaId)) {
        setActiveProductMedia(this.section, featuredMediaId, true);
      }

      this.updatePrice(option, variant);
      this.updateInventory(option, variant, available);
      this.syncOptionPickers(variant);

      const sku = option.dataset.sku || variant?.sku || '';
      const skuRoot = this.section.querySelector('[data-product-sku]');
      const skuValue = skuRoot?.querySelector('[data-product-sku-value]');
      if (skuValue) skuValue.textContent = sku;
      if (skuRoot) skuRoot.hidden = !sku;

      const submitText = available ? strings.addToCart || 'Add to cart' : strings.soldOut || 'Sold out';
      this.section.querySelectorAll('[data-product-submit], [data-sticky-atc-submit]').forEach((button) => {
        button.disabled = !available;
        button.setAttribute('aria-disabled', String(!available));
        const text = button.querySelector('[data-product-submit-text]');
        if (text) text.textContent = submitText;
        else button.textContent = submitText;
      });
      this.section.querySelectorAll('[data-product-submit-price]').forEach((price) => {
        const currentPrice = option.dataset.price || '';
        if (currentPrice) price.textContent = currentPrice;
        price.hidden = !available || !currentPrice;
      });

      const status = this.section.querySelector('[data-product-status]');
      if (status) {
        const selectedTemplate =
          strings.selectedVariant || '__VARIANT__ selected at __PRICE__. __AVAILABILITY__';
        status.textContent = selectedTemplate
          .replace('__VARIANT__', variant?.title || option.textContent.trim())
          .replace('__PRICE__', option.dataset.price || '')
          .replace('__AVAILABILITY__', available ? strings.inStock || 'In stock' : submitText);
      }
      const dynamicCheckout = this.section.querySelector('.product-form__dynamic-checkout');
      if (dynamicCheckout) dynamicCheckout.hidden = !available;
      const variantInput = this.section.querySelector('[data-variant-input]');
      if (variantInput) variantInput.value = option.value;

      const rule = variant?.quantity_rule;
      const quantity = this.section.querySelector('[data-quantity-input]');
      if (quantity && rule) {
        quantity.min = String(rule.min || 1);
        quantity.step = String(rule.increment || 1);
        if (rule.max) quantity.max = String(rule.max);
        else quantity.removeAttribute('max');
        if (Number(quantity.value) < Number(quantity.min)) quantity.value = quantity.min;
        if (quantity.max && Number(quantity.value) > Number(quantity.max)) quantity.value = quantity.max;
      }

      const variantUrl = option.dataset.variantUrl;
      if (variantUrl && window.history?.replaceState) {
        const nextUrl = new URL(variantUrl, location.origin);
        window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
      }
      this.section.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }

    openZoom(trigger, event) {
      const dialog = this.section.querySelector('[data-product-zoom-dialog]');
      if (!dialog) return;
      const openedByPointer = Boolean(event?.detail);
      dialog.classList.toggle('is-pointer-open', openedByPointer);
      if (openedByPointer) trigger.blur();
      const index = Number(trigger.dataset.zoomIndex || 0);
      this.showZoomSlide(Number.isFinite(index) ? index : 0, true);
      openDialog(dialog, trigger, dialog.querySelector('[data-product-zoom-close]'));
    }

    resetZoomViewer() {
      const dialog = this.section.querySelector('[data-product-zoom-dialog]');
      const slides = [...(dialog?.querySelectorAll('[data-product-zoom-slide]') || [])];
      slides.forEach((slide) => {
        slide.classList.remove('is-zoomed', 'is-dragging');
        const image = slide.querySelector('.product-zoom__image');
        image?.style.removeProperty('--product-zoom-scale');
        image?.style.removeProperty('--product-zoom-pan-x');
        image?.style.removeProperty('--product-zoom-pan-y');
        image?.style.removeProperty('--product-zoom-origin-x');
        image?.style.removeProperty('--product-zoom-origin-y');
      });
      this.zoomScale = 1;
      this.zoomPanX = 0;
      this.zoomPanY = 0;
    }

    showZoomSlide(index, instant = false) {
      const dialog = this.section.querySelector('[data-product-zoom-dialog]');
      const track = dialog?.querySelector('[data-product-zoom-track]');
      const slides = [...(dialog?.querySelectorAll('[data-product-zoom-slide]') || [])];
      if (!track || slides.length === 0) return;

      this.resetZoomViewer();
      const nextIndex = ((index % slides.length) + slides.length) % slides.length;
      this.zoomIndex = nextIndex;
      if (instant) track.style.transitionDuration = '0ms';
      track.style.transform = `translate3d(${-nextIndex * 100}%, 0, 0)`;
      if (instant) {
        window.requestAnimationFrame(() => track.style.removeProperty('transition-duration'));
      }

      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === nextIndex;
        slide.setAttribute('aria-hidden', String(!active));
        if (!active) slide.querySelectorAll('video').forEach((video) => video.pause());
      });
      const current = dialog.querySelector('[data-product-zoom-current]');
      if (current) current.textContent = String(nextIndex + 1);
    }

    toggleZoomImage(image, event) {
      const slide = image?.closest('[data-product-zoom-slide]');
      if (!slide) return;
      if (slide.classList.contains('is-zoomed')) {
        this.resetZoomViewer();
        return;
      }

      const bounds = image.getBoundingClientRect();
      const originX = bounds.width > 0 ? ((event.clientX - bounds.left) / bounds.width) * 100 : 50;
      const originY = bounds.height > 0 ? ((event.clientY - bounds.top) / bounds.height) * 100 : 50;
      let sourceWidth = image.naturalWidth;
      try {
        const candidateWidth = Number(new URL(image.currentSrc, window.location.href).searchParams.get('width'));
        if (Number.isFinite(candidateWidth)) sourceWidth = Math.max(sourceWidth, candidateWidth);
      } catch {
        sourceWidth = image.naturalWidth;
      }
      const naturalScale = image.clientWidth > 0 ? sourceWidth / image.clientWidth : 1;
      const scale = Math.max(1, Math.min(3, naturalScale));
      if (scale <= 1.05) return;

      this.zoomScale = scale;
      this.zoomPanX = 0;
      this.zoomPanY = 0;
      image.style.setProperty('--product-zoom-scale', String(scale));
      image.style.setProperty('--product-zoom-origin-x', `${Math.max(0, Math.min(100, originX))}%`);
      image.style.setProperty('--product-zoom-origin-y', `${Math.max(0, Math.min(100, originY))}%`);
      slide.classList.add('is-zoomed');
    }

    initZoomViewer() {
      const dialog = this.section.querySelector('[data-product-zoom-dialog]');
      const stage = dialog?.querySelector('[data-product-zoom-stage]');
      if (!dialog || !stage) return;

      this.zoomIndex = 0;
      this.zoomScale = 1;
      this.zoomPanX = 0;
      this.zoomPanY = 0;
      let pointerId = null;
      let startX = 0;
      let startY = 0;
      let startPanX = 0;
      let startPanY = 0;
      let didDrag = false;
      let pointerImage = null;

      dialog.querySelector('[data-product-zoom-previous]')?.addEventListener('click', () => {
        this.showZoomSlide(this.zoomIndex - 1);
      });
      dialog.querySelector('[data-product-zoom-next]')?.addEventListener('click', () => {
        this.showZoomSlide(this.zoomIndex + 1);
      });
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.showZoomSlide(this.zoomIndex - 1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.showZoomSlide(this.zoomIndex + 1);
        }
      });
      dialog.addEventListener('close', () => this.resetZoomViewer());

      stage.addEventListener('pointerdown', (event) => {
        if (event.button !== 0 && event.pointerType === 'mouse') return;
        if (event.target.closest('.product-zoom__image')) event.preventDefault();
        pointerId = event.pointerId;
        startX = event.clientX;
        startY = event.clientY;
        startPanX = this.zoomPanX || 0;
        startPanY = this.zoomPanY || 0;
        didDrag = false;
        pointerImage = event.target.closest('.product-zoom__image');
        stage.setPointerCapture?.(pointerId);
        if (this.zoomScale > 1) {
          event.target.closest('[data-product-zoom-slide]')?.classList.add('is-dragging');
        }
      });

      stage.addEventListener('pointermove', (event) => {
        if (pointerId !== event.pointerId) return;
        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;
        if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) didDrag = true;
        if (this.zoomScale <= 1) return;

        const slide = dialog.querySelector('[data-product-zoom-slide]:not([aria-hidden="true"])');
        const image = slide?.querySelector('.product-zoom__image');
        if (!image) return;
        const limitX = (image.clientWidth * (this.zoomScale - 1)) / 2;
        const limitY = (image.clientHeight * (this.zoomScale - 1)) / 2;
        this.zoomPanX = Math.max(-limitX, Math.min(limitX, startPanX + deltaX));
        this.zoomPanY = Math.max(-limitY, Math.min(limitY, startPanY + deltaY));
        image.style.setProperty('--product-zoom-pan-x', `${this.zoomPanX}px`);
        image.style.setProperty('--product-zoom-pan-y', `${this.zoomPanY}px`);
      });

      const finishPointer = (event) => {
        if (pointerId !== event.pointerId) return;
        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;
        dialog.querySelectorAll('.product-zoom__slide.is-dragging').forEach((slide) => {
          slide.classList.remove('is-dragging');
        });
        stage.releasePointerCapture?.(pointerId);
        pointerId = null;
        if (this.zoomScale <= 1 && Math.abs(deltaX) > 52 && Math.abs(deltaX) > Math.abs(deltaY)) {
          this.showZoomSlide(this.zoomIndex + (deltaX < 0 ? 1 : -1));
        }
      };
      stage.addEventListener('pointerup', finishPointer);
      stage.addEventListener('pointercancel', finishPointer);
      stage.addEventListener('click', (event) => {
        const image = event.target.closest('.product-zoom__image') || pointerImage;
        pointerImage = null;
        if (!image || didDrag) {
          didDrag = false;
          return;
        }
        this.toggleZoomImage(image, event);
      });
    }

    initGalleryCarousel() {
      const viewport = this.section.querySelector('.product-gallery__viewport');
      const slides = [...this.section.querySelectorAll('[data-product-media]')];
      if (!viewport || slides.length === 0) return;

      let frame = 0;
      let lastActiveSlide = null;
      let galleryWidth = viewport.clientWidth;
      const gap = Number.parseFloat(getComputedStyle(viewport).columnGap) || 0;
      const initial = slides.find(slide => slide.classList.contains('is-active'));
      if (initial && initial !== slides[0]) {
        viewport.scrollTo({ left: initial.offsetLeft - slides[0].offsetLeft, behavior: 'instant' });
      }
      const syncActiveSlide = () => {
        frame = 0;
        const width = viewport.clientWidth;
        const stride = width + gap;
        if (!stride) return;
        if (width !== galleryWidth && lastActiveSlide) {
          viewport.scrollTo({ left: slides.indexOf(lastActiveSlide) * stride, behavior: 'instant' });
        }
        galleryWidth = width;
        const index = Math.max(0, Math.min(slides.length - 1, Math.round(viewport.scrollLeft / stride)));
        const activeSlide = slides[index];
        if (activeSlide === lastActiveSlide) return;
        lastActiveSlide = activeSlide;
        updateProductGalleryCounter(this.section, activeSlide);

        slides.forEach((slide) => {
          const active = slide === activeSlide;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
          if (!active) slide.querySelectorAll('video').forEach((video) => video.pause());
        });

        this.section.querySelectorAll('[data-media-thumbnail]').forEach((thumbnail) => {
          const controlled = thumbnail.getAttribute('aria-controls') || thumbnail.dataset.mediaTarget || '';
          const active = controlled === activeSlide.id;
          thumbnail.classList.toggle('is-active', active);
          thumbnail.setAttribute('aria-current', String(active));
        });
      };
      const scheduleSync = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(syncActiveSlide);
      };

      viewport.addEventListener('scroll', scheduleSync, { passive: true });
      window.addEventListener('resize', scheduleSync, { passive: true });
      syncActiveSlide();

      this.section._whiteAppleGalleryCleanup = () => {
        if (frame) window.cancelAnimationFrame(frame);
        viewport.removeEventListener('scroll', scheduleSync);
        window.removeEventListener('resize', scheduleSync);
      };
    }

    initSticky() {
      const sticky = this.section.querySelector('[data-sticky-atc]');
      const primary = this.section.querySelector('[data-product-submit]');
      if (!sticky || !primary) return;
      const mobile = window.matchMedia('(max-width: 749px)');
      let frame = 0;
      let visible = false;
      const update = () => {
        const nextVisible = mobile.matches && primary.getBoundingClientRect().bottom <= 0;
        sticky.classList.toggle('is-visible', nextVisible);
        sticky.setAttribute('aria-hidden', String(!nextVisible));
        document.body.classList.toggle('has-product-sticky-atc', nextVisible);
        if (visible !== nextVisible) {
          visible = nextVisible;
          document.dispatchEvent(
            new CustomEvent('product-sticky-atc:change', { detail: { visible: nextVisible } })
          );
        }
        frame = 0;
      };
      const schedule = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(update);
      };
      const observer = window.IntersectionObserver
        ? new IntersectionObserver(schedule, { threshold: [0, 0.15, 1] })
        : null;
      observer?.observe(primary);
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      mobile.addEventListener?.('change', schedule);
      update();
      this.section._whiteAppleStickyCleanup = () => {
        if (frame) window.cancelAnimationFrame(frame);
        observer?.disconnect();
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', schedule);
        mobile.removeEventListener?.('change', schedule);
        sticky.classList.remove('is-visible');
        sticky.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('has-product-sticky-atc');
        if (visible) {
          document.dispatchEvent(
            new CustomEvent('product-sticky-atc:change', { detail: { visible: false } })
          );
        }
      };
    }

    destroy() {
      this.section._whiteAppleGalleryCleanup?.();
      this.section._whiteAppleStickyCleanup?.();
    }
  }

  const safeProductUrl = (value) => {
    try {
      const url = new URL(String(value || ''), location.origin);
      if (url.origin !== location.origin || !url.pathname.includes('/products/')) return '';
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return '';
    }
  };

  const safeImageUrl = (value) => {
    try {
      const url = new URL(String(value || ''), location.origin);
      return url.protocol === 'https:' || url.origin === location.origin ? url.href : '';
    } catch {
      return '';
    }
  };

  const productCondition = (product) => {
    const condition = String(product.condition || '').trim().slice(0, 80);
    const grade = String(product.condition_grade || '').trim().slice(0, 80);
    return {
      condition,
      label: grade && condition.toLowerCase() !== 'new' ? `${condition} · ${grade}` : condition,
      handle:
        condition
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '') || 'default'
    };
  };

  const createRecentCard = (product, showVendor, showFreeDelivery) => {
    const url = safeProductUrl(product.url);
    if (!url) return null;
    const isSmartphone =
      String(product.product_type || '').toLowerCase() === 'smartphones';
    const card = document.createElement('article');
    card.className = `product-card${isSmartphone ? ' product-card--smartphone' : ''}${product.available ? '' : ' product-card--sold-out'}`;
    card.dataset.productCard = '';
    card.dataset.productId = String(product.id);

    const mediaWrap = document.createElement('div');
    mediaWrap.className = 'product-card__media-wrap';
    const mediaLink = document.createElement('a');
    mediaLink.className = 'product-card__media';
    mediaLink.href = url;
    mediaLink.setAttribute('aria-label', String(product.title || ''));
    const imageUrl = safeImageUrl(product.featured_image);
    if (imageUrl) {
      const image = document.createElement('img');
      image.className = 'product-card__image product-card__image--primary';
      image.src = imageUrl;
      image.alt = String(product.title || '');
      image.loading = 'lazy';
      image.width = 900;
      image.height = 900;
      mediaLink.append(image);
    }
    mediaWrap.append(mediaLink);
    const condition = productCondition(product);
    if (!product.available || condition.label) {
      const badges = document.createElement('div');
      badges.className = 'product-card__badges';
      if (condition.label) {
        const conditionBadge = document.createElement('span');
        conditionBadge.className = `badge badge--condition badge--condition-${condition.handle}`;
        conditionBadge.textContent = condition.label;
        badges.append(conditionBadge);
      }
      if (!product.available) {
        const badge = document.createElement('span');
        badge.className = 'badge badge--sold-out';
        badge.textContent = strings.soldOut || 'Sold out';
        badges.append(badge);
      }
      mediaWrap.append(badges);
    }

    const content = document.createElement('div');
    content.className = 'product-card__content';
    const details = document.createElement('div');
    details.className = 'product-card__details';
    if (showVendor && product.vendor) {
      const vendor = document.createElement('p');
      vendor.className = 'eyebrow product-card__vendor';
      const vendorLink = document.createElement('a');
      const category = isSmartphone ? 'smartphones' : 'accessories';
      const collectionUrl = theme.brandCollections?.[category]?.[String(product.vendor)];
      const vendorUrl = new URL(collectionUrl || `${window.Shopify?.routes?.root || '/'}collections/${category}`, location.origin);
      if (!collectionUrl) vendorUrl.searchParams.set('filter.p.vendor', String(product.vendor));
      vendorLink.href = `${vendorUrl.pathname}${vendorUrl.search}`;
      vendorLink.textContent = String(product.vendor);
      vendor.append(vendorLink);
      details.append(vendor);
    }
    const title = document.createElement('h3');
    title.className = 'product-card__title';
    const titleLink = document.createElement('a');
    titleLink.href = url;
    titleLink.textContent = String(product.title || '');
    title.append(titleLink);
    details.append(title);
    const price = document.createElement('div');
    price.className = 'price';
    price.dataset.price = '';
    const current = document.createElement('span');
    current.className = 'price__current';
    current.textContent = String(product.price || '');
    price.append(current);
    details.append(price);
    if (showFreeDelivery) {
      const delivery = document.createElement('p');
      delivery.className = 'product-card__delivery';
      delivery.textContent = strings.freeDelivery || 'Free delivery';
      details.append(delivery);
    }
    content.append(details);
    const actions = document.createElement('div');
    actions.className = 'product-card__actions';
    const view = document.createElement('a');
    view.className = 'button button--dark product-card__choose-options';
    view.href = url;
    view.setAttribute(
      'aria-label',
      `${strings.chooseOptions || 'Select options'}: ${String(product.title || '')}`
    );
    const viewLabel = document.createElement('span');
    viewLabel.className = 'product-card__action-label';
    viewLabel.textContent = strings.chooseOptions || 'Select options';
    const viewIconWrap = document.createElement('span');
    viewIconWrap.className = 'product-card__action-icon';
    viewIconWrap.setAttribute('aria-hidden', 'true');
    const viewIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    viewIcon.setAttribute('class', 'icon icon--cart');
    viewIcon.setAttribute('aria-hidden', 'true');
    viewIcon.setAttribute('viewBox', '0 0 24 24');
    const viewIconLine = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    viewIconLine.setAttribute(
      'd',
      'M1 1h.5v0c.226 0 .339 0 .44.007a3 3 0 0 1 2.62 1.976c.034.095.065.204.127.42l.17.597m0 0 1.817 6.358c.475 1.664.713 2.496 1.198 3.114a4 4 0 0 0 1.633 1.231c.727.297 1.592.297 3.322.297h2.285c1.75 0 2.626 0 3.359-.302a4 4 0 0 0 1.64-1.253c.484-.627.715-1.472 1.175-3.161l.06-.221c.563-2.061.844-3.092.605-3.906a3 3 0 0 0-1.308-1.713C19.92 4 18.853 4 16.716 4H4.857ZM12 20a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm8 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z'
    );
    viewIcon.append(viewIconLine);
    viewIconWrap.append(viewIcon);
    view.append(viewLabel, viewIconWrap);
    actions.append(view);
    mediaWrap.append(actions);
    card.append(mediaWrap, content);
    return card;
  };

  const createCartRecentItem = (product) => {
    const url = safeProductUrl(product.url);
    if (!url) return null;
    const item = document.createElement('article');
    item.className = 'cart-drawer__recent-item';
    item.dataset.productId = String(product.id || '');

    const media = document.createElement('a');
    media.className = 'cart-drawer__recent-media';
    media.href = url;
    media.setAttribute('aria-label', String(product.title || ''));
    const imageUrl = safeImageUrl(product.featured_image);
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = String(product.title || '');
      image.loading = 'lazy';
      image.width = 160;
      image.height = 160;
      media.append(image);
    }

    const copy = document.createElement('div');
    copy.className = 'cart-drawer__recent-copy';
    const title = document.createElement('a');
    title.className = 'cart-drawer__recent-title';
    title.href = url;
    title.textContent = String(product.title || '');
    const price = document.createElement('span');
    price.className = 'cart-drawer__recent-price';
    price.textContent = String(product.price || '');
    copy.append(title, price);

    const action = document.createElement('a');
    action.className = 'button button--dark cart-drawer__recent-action';
    action.href = url;
    action.setAttribute(
      'aria-label',
      `${strings.viewProduct || 'View product'}: ${String(product.title || '')}`
    );
    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('viewBox', '0 0 12 12');
    icon.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M2.5 6h7M6 2.5v7');
    icon.append(path);
    const label = document.createElement('span');
    label.className = 'cart-drawer__recent-action-label';
    label.textContent = strings.view || 'View';
    action.append(icon, label);

    item.append(media, copy, action);
    return item;
  };

  const loadRecentCard = async (product, section) => {
    const productUrl = safeProductUrl(product.url);
    if (!productUrl) return null;
    const showVendor = section.dataset.showVendor === 'true';
    const showFreeDelivery = section.dataset.showFreeDelivery === 'true';
    try {
      const productPath = new URL(productUrl, location.origin).pathname.split('/products/')[1];
      const url = new URL(`${window.Shopify?.routes?.root || '/'}products/${productPath}`, location.origin);
      url.searchParams.set('section_id', 'recently-viewed-card');
      const response = await fetch(url, { signal: AbortSignal.timeout(8000) });
      if (response.status === 404) return null;
      if (!response.ok) throw new Error('Recent product unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const card = html.querySelector('[data-product-card]');
      if (!card) return null;
      if (!showVendor) card.querySelector('.product-card__vendor')?.remove();
      if (!showFreeDelivery) {
        card.querySelector('.product-card__highlight .icon--truck')?.closest('.product-card__highlight')?.remove();
        const highlights = card.querySelector('.product-card__highlights');
        if (highlights && !highlights.children.length) highlights.remove();
      }
      return card;
    } catch {
      return createRecentCard(product, showVendor, showFreeDelivery);
    }
  };

  const initRecentlyViewed = (section) => {
    const script = section.querySelector('[data-recent-product]');
    const grid = section.querySelector('[data-recently-viewed-grid]');
    if (!script || !grid) return;
    let current;
    try {
      current = JSON.parse(script.textContent);
    } catch {
      return;
    }
    const key = 'whiteAppleRecentlyViewed';
    let stored = [];
    try {
      const value = JSON.parse(localStorage.getItem(key) || '[]');
      if (Array.isArray(value)) stored = value;
    } catch {
      stored = [];
    }
    const currentId = String(section.dataset.currentProductId || current.id || '');
    const previous = stored
      .filter((product) => product && String(product.id) !== currentId)
      .slice(0, 12);
    const visibleProducts = previous.slice(0, Math.max(1, Number(section.dataset.limit) || 4));
    Promise.all(visibleProducts.map((product) => loadRecentCard(product, section))).then((cards) => {
      if (!section.isConnected) return;
      const items = cards.filter(Boolean).map((card) => {
        const item = document.createElement('div');
        item.className = 'product-grid__item';
        item.setAttribute('role', 'listitem');
        item.append(card);
        return item;
      });
      grid.replaceChildren(...items);
      section.hidden = !items.length;
      section._whiteAppleCarouselCleanup?.();
      delete section.dataset.runtimeCollectionCarousel;
      initFeaturedCollectionCarousel(section);
      initTree(grid);
    });
    const safeCurrent = {
      id: String(current.id || ''),
      variant_id: String(current.variant_id || ''),
      handle: String(current.handle || '').slice(0, 200),
      url: safeProductUrl(current.url),
      title: String(current.title || '').slice(0, 300),
      vendor: String(current.vendor || '').slice(0, 200),
      product_type: String(current.product_type || '').slice(0, 200),
      condition: String(current.condition || '').slice(0, 80),
      condition_grade: String(current.condition_grade || '').slice(0, 80),
      featured_image: safeImageUrl(current.featured_image),
      price: String(current.price || '').slice(0, 100),
      available: Boolean(current.available)
    };
    if (safeCurrent.id && safeCurrent.url) {
      try {
        localStorage.setItem(key, JSON.stringify([safeCurrent, ...previous].slice(0, 12)));
      } catch {
        return;
      }
    }
  };

  const initCartDrawer = (drawer) => {
    if (drawer.dataset.runtimeCartDrawer === 'true') return;
    drawer.dataset.runtimeCartDrawer = 'true';
    drawer._cartRecommendationResize?.disconnect();
    drawer._cartRecommendationResize = null;
    const surface = drawer.querySelector('[data-dialog-surface]');
    initSheetDismiss(drawer, surface, () => closeDialog(drawer), '(max-width: 749px)');
    const track = drawer.querySelector('[data-cart-recommendation-track]');
    if (track) {
      track.querySelectorAll('img').forEach(image => window.WhiteAppleTheme.fitProductImage?.(image));
      const update = () => {
        const previous = drawer.querySelector('[data-cart-recommendation-prev]');
        const next = drawer.querySelector('[data-cart-recommendation-next]');
        if (previous) previous.disabled = track.scrollLeft <= 1;
        if (next) next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
      };
      track.addEventListener('scroll', update, { passive: true });
      const resize = new ResizeObserver(update);
      resize.observe(track);
      drawer._cartRecommendationResize?.disconnect();
      drawer._cartRecommendationResize = resize;
      update();
      if (!track.children.length) track.closest('.cart-drawer__recommendations').hidden = true;
    }
    const grid = drawer.querySelector('[data-cart-recent-grid]');
    const empty = drawer.querySelector('[data-cart-recent-empty]');
    if (!grid || !empty) return;

    let recent = [];
    try {
      const stored = JSON.parse(localStorage.getItem('whiteAppleRecentlyViewed') || '[]');
      if (Array.isArray(stored)) recent = stored.slice(0, 5);
    } catch {
      recent = [];
    }

    recent.forEach((product) => {
      const item = createCartRecentItem(product);
      if (item) grid.append(item);
    });
    grid.querySelectorAll('img').forEach(image => window.WhiteAppleTheme.fitProductImage?.(image));
    empty.hidden = grid.children.length > 0;
  };

  const initRecommendations = (section) => {
    if (!section.hidden || section.dataset.runtimeRecommendations === 'true' || !section.dataset.url) return;
    section.dataset.runtimeRecommendations = 'true';
    fetch(section.dataset.url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((markup) => {
        if (!section.isConnected) return;
        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const replacement = parsed.querySelector('[data-product-recommendations]');
        if (!replacement || replacement.hidden || !replacement.textContent.trim()) return;
        section.replaceWith(replacement);
        initTree(replacement);
      })
      .catch(() => undefined);
  };

  const scrollFeaturedCollection = (button) => {
    if (button?.classList.contains('is-disabled')) return;
    const section = button?.closest('[data-featured-carousel-section]');
    const track = section?.querySelector('[data-collection-carousel]');
    const firstCard = track?.firstElementChild;
    if (!track || !firstCard) return;
    const style = window.getComputedStyle(track);
    const gap = Number.parseFloat(style.columnGap || style.gap) || 0;
    const cardDistance = firstCard.getBoundingClientRect().width + gap;
    const distance = Math.max(cardDistance, track.clientWidth);
    const direction = button.matches('[data-collection-carousel-prev]') ? -1 : 1;
    track.scrollBy({
      left: direction * distance,
      behavior: 'auto'
    });
  };

  const initFeaturedCollectionCarousel = (section) => {
    if (section.dataset.runtimeCollectionCarousel === 'true') return;
    section.dataset.runtimeCollectionCarousel = 'true';
    const track = section.querySelector('[data-collection-carousel]');
    const previous = section.querySelector('[data-collection-carousel-prev]');
    const next = section.querySelector('[data-collection-carousel-next]');
    if (!track || !previous || !next) return;
    let frame = 0;

    const update = () => {
      const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
      const position = Math.max(0, track.scrollLeft);
      const atStart = maximum <= 2 || position <= 2;
      const atEnd = maximum <= 2 || position >= maximum - 2;
      previous.classList.toggle('is-disabled', atStart);
      next.classList.toggle('is-disabled', atEnd);
      frame = 0;
    };
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    track.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    const resizeObserver = window.ResizeObserver ? new ResizeObserver(schedule) : null;
    resizeObserver?.observe(track);
    [...track.children].forEach((card) => resizeObserver?.observe(card));
    update();

    section._whiteAppleCarouselCleanup = () => {
      if (frame) window.cancelAnimationFrame(frame);
      track.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      resizeObserver?.disconnect();
    };
  };

  const initReveal = (root) => {
    const revealElements = elements(root, '.reveal').filter((element) => element.dataset.runtimeReveal !== 'true');
    if (!revealElements.length) return;
    if (
      document.body.dataset.animations !== 'true' ||
      reduceMotion.matches ||
      !window.IntersectionObserver
    ) {
      revealElements.forEach((element) => {
        element.dataset.runtimeReveal = 'true';
        element.classList.add('is-visible');
      });
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
    );
    revealElements.forEach((element) => {
      element.dataset.runtimeReveal = 'true';
      observer.observe(element);
    });
  };

  const initBackToTop = (button) => {
    if (button.dataset.runtimeBackToTop === 'true') return;
    button.dataset.runtimeBackToTop = 'true';
    let frame = null;
    const update = () => {
      frame = null;
      const visible = window.scrollY > Math.max(500, window.innerHeight);
      button.classList.toggle('is-visible', visible);
      button.setAttribute('aria-hidden', String(!visible));
      button.tabIndex = visible ? 0 : -1;
      const footerUtility = document.querySelector('.footer-utility');
      document.body.classList.toggle('has-footer-controls', Boolean(footerUtility && footerUtility.getBoundingClientRect().top < window.innerHeight));
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!frame) frame = window.requestAnimationFrame(update);
      },
      { passive: true }
    );
    button.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    });
    const fill = button.querySelector('.back-to-top__fill');
    let sweepAnimation;
    const sweep = (event, entering) => {
      if (!fill || event.pointerType === 'touch' || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      const from = entering ? 'translateY(76%)' : getComputedStyle(fill).transform;
      sweepAnimation?.cancel();
      sweepAnimation = fill.animate([
        { transform: from },
        { transform: entering ? 'translateY(0)' : 'translateY(-76%)' }
      ], { duration: reduceMotion.matches ? 0 : 600, easing: 'ease', fill: 'forwards' });
    };
    button.addEventListener('pointerenter', event => sweep(event, true));
    button.addEventListener('pointerleave', event => sweep(event, false));
    update();
  };

  const initShare = (button) => {
    if (button.dataset.runtimeShare === 'true') return;
    button.dataset.runtimeShare = 'true';
    button.addEventListener('click', async () => {
      const data = {
        title: button.dataset.shareTitle || document.title,
        url: button.dataset.shareUrl || location.href
      };
      try {
        if (navigator.share) {
          await navigator.share(data);
          return;
        }
        await navigator.clipboard.writeText(data.url);
        announce(strings.linkCopied || 'Link copied');
      } catch (error) {
        if (error.name !== 'AbortError') announce(strings.shareError || 'Unable to share this link');
      }
    });
  };

  const initMobileLanguage = (dialog) => {
    if (dialog.dataset.runtimeMobileLanguage) return;
    dialog.dataset.runtimeMobileLanguage = 'true';
    const opener = document.querySelector(`[data-mobile-language-open][aria-controls="${dialog.id}"]`);
    opener?.addEventListener('click', (event) => {
      opener.setAttribute('aria-expanded', 'true');
      dialog.classList.toggle('is-pointer-open', event.detail > 0);
      // Pointer users keep focus on the dialog, avoiding a scroll to the
      // initially offscreen close button. Keyboard users reach the active option.
      const focusTarget = event.detail > 0 ? dialog : dialog.querySelector('[data-language-current]');
      openDialog(dialog, opener, focusTarget);
    });
    dialog.addEventListener('submit', (event) => {
      if (event.submitter?.matches('[data-language-current]')) {
        event.preventDefault();
        closeDialog(dialog);
      }
    });
  };

  const initMobileMenu = (details) => {
    if (details.dataset.runtimeMobileMenu === 'true') return;
    details.dataset.runtimeMobileMenu = 'true';
    const summary = details.querySelector(':scope > summary');
    const panel = details.querySelector('[data-mobile-menu-panel]');
    const owningHeader = details.closest('[data-site-header]');
    const mobileDock = document.querySelector(`[data-mobile-dock="${owningHeader?.dataset.sectionId || ''}"]`);
    const externalOpeners = [...document.querySelectorAll('[data-mobile-menu-open]')].filter(
      (opener) => opener.dataset.mobileMenuOpen === details.id
    );
    let closeTimer = 0;
    if (!panel) return;
    panel.querySelectorAll('ul').forEach((list) => {
      [...list.children].forEach((item, index) => item.style.setProperty('--menu-order', Math.min(index, 8)));
    });

    const closeMenu = (restoreFocus = true) => {
      if (!details.open) return;
      window.clearTimeout(closeTimer);
      details.classList.remove('is-active');
      externalOpeners.forEach((opener) => opener.setAttribute('aria-expanded', 'false'));
      closeTimer = window.setTimeout(
        () => {
          details.removeAttribute('open');
          if (restoreFocus) summary?.focus({ preventScroll: true });
        },
        reduceMotion.matches ? 0 : 800
      );
    };

    mobileMenuClosers.set(details, closeMenu);
    summary?.addEventListener('click', (event) => {
      event.preventDefault();
      const openedByPointer = event.detail > 0;
      if (details.open) {
        closeMenu(!openedByPointer);
        return;
      }
      details.setAttribute('open', '');
      if (openedByPointer) summary.blur();
    });
    initSheetDismiss(
      details,
      panel,
      () => closeMenu(true),
      '(max-width: 1099px)'
    );
    details.addEventListener('toggle', () => {
      externalOpeners.forEach((opener) => opener.setAttribute('aria-expanded', String(details.open)));
      if (details.open) {
        window.clearTimeout(closeTimer);
        details.classList.remove('is-active');
        owningHeader?.classList.remove('site-header--mobile-hidden');
        mobileDock?.classList.remove('mobile-dock--visible');
        document.body.classList.remove('has-mobile-dock-visible');
        document.querySelectorAll('[data-mobile-menu][open]').forEach((other) => {
          if (other !== details) other.removeAttribute('open');
        });
        syncBodyLock();
        panel.getBoundingClientRect();
        details.classList.add('is-active');
        window.setTimeout(
          () => {
            if (!details.open || !details.classList.contains('is-active') || document.querySelector('dialog[open]')) return;
            (panel.querySelector('[data-sheet-drag-handle]') || focusableElements(panel)[0])?.focus({
              preventScroll: true
            });
          },
          reduceMotion.matches ? 0 : 650
        );
      } else {
        details.classList.remove('is-active');
        syncBodyLock();
      }
    });
    panel.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu(true);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = focusableElements(panel);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
    panel.addEventListener('click', (event) => {
      if (event.target.closest('a[href]')) closeMenu(false);
    });
  };

  const initSiteHeader = (header) => {
    if (header.dataset.runtimeSiteHeader === 'true') return;
    header.dataset.runtimeSiteHeader = 'true';
    const mobileDock = document.querySelector(`[data-mobile-dock="${header.dataset.sectionId}"]`);
    if (mobileDock) {
      const dockSizeObserver = new ResizeObserver(() => {
        document.body.style.setProperty('--mobile-dock-height', `${mobileDock.getBoundingClientRect().height}px`);
      });
      dockSizeObserver.observe(mobileDock);
    }
    let frame = 0;
    let previousScrollY = Math.max(0, window.scrollY);
    let mobileHidden = false;
    let dockClearanceTimer = 0;
    let dockWasVisible = false;
    const update = () => {
      // Ignore elastic overscroll when deciding which way the page is moving.
      const maximumScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const currentScrollY = Math.min(maximumScrollY, Math.max(0, window.scrollY));
      const stuck = currentScrollY > 0 && header.parentElement.getBoundingClientRect().top <= 0;
      header.classList.toggle('site-header--scrolled', currentScrollY > 8);
      header.classList.toggle('site-header--stuck', stuck);
      if (window.innerWidth <= 1099) {
        if (currentScrollY <= 8 || currentScrollY >= maximumScrollY - 2) {
          mobileHidden = false;
        } else if (currentScrollY > previousScrollY + 4 && currentScrollY > 120) {
          mobileHidden = true;
        } else if (currentScrollY < previousScrollY - 4) {
          mobileHidden = false;
        }
      } else {
        mobileHidden = false;
      }
      if (header.querySelector('[data-mobile-menu][open]')) mobileHidden = false;
      header.classList.toggle('site-header--mobile-hidden', mobileHidden);
      const showMobileDock =
        mobileHidden && !document.body.classList.contains('has-product-sticky-atc');
      mobileDock?.classList.toggle('mobile-dock--visible', showMobileDock);
      document.body.classList.toggle('has-mobile-dock-visible', showMobileDock);
      // Reserve the dock's full height before it enters and until its exit has finished.
      if (showMobileDock) {
        window.clearTimeout(dockClearanceTimer);
        document.body.classList.add('has-mobile-dock-clearance');
      } else if (dockWasVisible) {
        window.clearTimeout(dockClearanceTimer);
        dockClearanceTimer = window.setTimeout(() => {
          document.body.classList.remove('has-mobile-dock-clearance');
        }, reduceMotion.matches ? 0 : 520);
      }
      dockWasVisible = showMobileDock;
      previousScrollY = currentScrollY;
      frame = 0;
    };
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('product-sticky-atc:change', schedule);
    update();
  };

  const initDesktopNavDisclosure = (details) => {
    if (details.dataset.runtimeDesktopHover === 'true') return;
    details.dataset.runtimeDesktopHover = 'true';
    const summary = details.querySelector(':scope > summary');
    const hoverQuery = window.matchMedia('(min-width: 1100px) and (hover: hover) and (pointer: fine)');
    let openTimer = 0;
    let closeTimer = 0;

    const clearOpenTimer = () => {
      window.clearTimeout(openTimer);
      openTimer = 0;
    };

    const clearCloseTimer = () => {
      window.clearTimeout(closeTimer);
      closeTimer = 0;
    };

    const scheduleOpen = () => {
      if (!hoverQuery.matches) return;
      clearOpenTimer();
      clearCloseTimer();
      openTimer = window.setTimeout(() => {
        details.dataset.hoverOpened = 'true';
        details.setAttribute('open', '');
      }, 260);
    };

    const scheduleClose = () => {
      if (!hoverQuery.matches) return;
      clearOpenTimer();
      clearCloseTimer();
      closeTimer = window.setTimeout(() => {
        details.removeAttribute('open');
        delete details.dataset.hoverOpened;
      }, 300);
    };

    details.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'touch') return;
      scheduleOpen();
    });

    details.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'touch') return;
      scheduleClose();
    });

    details.addEventListener('focusout', (event) => {
      if (!details.contains(event.relatedTarget) && !details.matches(':hover')) scheduleClose();
    });

    summary?.addEventListener('click', (event) => {
      if (!hoverQuery.matches || !details.open || details.dataset.hoverOpened !== 'true' || event.detail === 0) {
        delete details.dataset.hoverOpened;
        return;
      }
      event.preventDefault();
      clearCloseTimer();
    });

    details.addEventListener('toggle', () => {
      if (!details.open) delete details.dataset.hoverOpened;
    });
  };

  const initKeywordSearch = (form) => {
    if (form.dataset.runtimeKeywordSearch) return;
    form.dataset.runtimeKeywordSearch = 'true';
    const input = form.querySelector('[name="q"]');
    if (!input) return;
    // Change submitted data without putting query syntax into the visible input
    // or leaving a rewritten value behind when navigating back.
    form.addEventListener('formdata', (event) => {
      event.formData.set('q', buildKeywordQuery(input.value));
    });
    // Older bookmarks and plain /search links should use the same matching.
    if (form.dataset.searchPerformed === 'true') {
      const url = new URL(location.href);
      const query = url.searchParams.get('q') || '';
      if (query.trim() && !query.startsWith('(title:"') && !/\b[\w.]+:/.test(query)) {
        const keywords = buildKeywordQuery(query);
        if (keywords) {
          url.searchParams.set('q', keywords);
          url.searchParams.set('type', 'product');
          url.searchParams.delete('page');
          location.replace(url.href);
        }
      }
    }
  };

  const initTree = (root = document) => {
    elements(root, '[data-keyword-search]').forEach(initKeywordSearch);
    elements(root, 'dialog').forEach(initDialog);
    elements(root, '[data-mobile-language-dialog]').forEach(initMobileLanguage);
    elements(root, '[data-announcement-bar]').forEach((element) => {
      if (element.dataset.runtimeAnnouncement === 'true') return;
      element.dataset.runtimeAnnouncement = 'true';
      element._whiteAppleRuntime = new AnnouncementRotator(element);
    });
    elements(root, '[data-predictive-search]').forEach((element) => {
      if (element.dataset.runtimePredictiveSearch === 'true') return;
      element.dataset.runtimePredictiveSearch = 'true';
      element._whiteAppleRuntime = new PredictiveSearch(element);
      const surface = element.querySelector('[data-dialog-surface]');
      initSheetDismiss(element, surface, () => closeDialog(element), '(max-width: 749px)');
    });
    elements(root, '[data-cart-drawer]').forEach(initCartDrawer);
    elements(root, '[data-mobile-menu]').forEach(initMobileMenu);
    elements(root, '[data-site-header]').forEach(initSiteHeader);
    elements(root, '[data-nav-disclosure]').forEach(initDesktopNavDisclosure);
    elements(root, '[data-featured-carousel-section]').forEach(initFeaturedCollectionCarousel);
    elements(root, '[data-nav-disclosure], [data-mobile-submenu]').forEach((details) => {
      if (details.dataset.runtimeDisclosure === 'true') return;
      details.dataset.runtimeDisclosure = 'true';
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        const groupSelector = details.matches('[data-mobile-submenu]') ? '[data-mobile-submenu][open]' : '[data-nav-disclosure][open]';
        details.closest('nav')?.querySelectorAll(groupSelector).forEach((other) => {
          if (other !== details && !other.contains(details)) other.removeAttribute('open');
        });
      });
    });
    elements(root, '[data-product-section]').forEach((section) => {
      if (section.dataset.runtimeProduct === 'true') return;
      section.dataset.runtimeProduct = 'true';
      section._whiteAppleRuntime = new ProductRuntime(section);
    });
    elements(root, '[data-product-recommendations]').forEach(initRecommendations);
    elements(root, '[data-recently-viewed]').forEach((section) => {
      if (section.dataset.runtimeRecentlyViewed === 'true') return;
      section.dataset.runtimeRecentlyViewed = 'true';
      initRecentlyViewed(section);
    });
    elements(root, '[data-collection-facets-form]').forEach(initCollectionFacets);
    elements(root, '[data-sort-select]').forEach((select) => {
      if (select.dataset.runtimeSort === 'true') return;
      select.dataset.runtimeSort = 'true';
      select.addEventListener('change', () => {
        const form = select.form || select.closest('form');
        if (form?.requestSubmit) form.requestSubmit();
        else form?.submit();
      });
    });
    elements(root, '[data-localization-select]').forEach((select) => {
      if (select.dataset.runtimeLocalization === 'true') return;
      select.dataset.runtimeLocalization = 'true';
      select.addEventListener('change', () => {
        const form = select.form || select.closest('form');
        if (form?.requestSubmit) form.requestSubmit();
        else form?.submit();
      });
    });
    elements(root, '[data-back-to-top]').forEach(initBackToTop);
    elements(root, '[data-share-url]').forEach(initShare);
    initReveal(root);
  };

  document.addEventListener('click', (event) => {
    const collectionCarouselButton = event.target.closest(
      '[data-collection-carousel-prev], [data-collection-carousel-next]'
    );
    if (collectionCarouselButton) {
      event.preventDefault();
      scrollFeaturedCollection(collectionCarouselButton);
      return;
    }

    const submenuBack = event.target.closest('[data-mobile-submenu-back]');
    if (submenuBack) {
      const disclosure = submenuBack.closest('[data-mobile-submenu]');
      if (disclosure) {
        event.preventDefault();
        disclosure.removeAttribute('open');
        disclosure.querySelector(':scope > summary')?.focus();
      }
      return;
    }

    const menuOpener = event.target.closest('[data-mobile-menu-open]');
    if (menuOpener) {
      const details = document.getElementById(menuOpener.dataset.mobileMenuOpen);
      if (details?.matches('[data-mobile-menu]')) {
        event.preventDefault();
        details.setAttribute('open', '');
      }
      return;
    }

    const searchOpener = event.target.closest('[data-search-dialog-open]');
    if (searchOpener) {
      const dialog = document.querySelector('[data-search-dialog]');
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        const openedByPointer = event.detail > 0;
        dialog.classList.toggle('is-pointer-open', openedByPointer);
        if (openedByPointer) searchOpener.blur();
        openDialog(dialog, searchOpener, dialog.querySelector('[data-predictive-search-input]'));
      }
      return;
    }

    const cartOpener = event.target.closest('[data-cart-drawer-open]');
    if (cartOpener) {
      const drawer = cart.drawer();
      if (drawer && typeof drawer.showModal === 'function') {
        event.preventDefault();
        const openedByPointer = event.detail > 0;
        drawer.classList.toggle('is-pointer-open', openedByPointer);
        if (openedByPointer) cartOpener.blur();
        cart.open(cartOpener, drawer.querySelector('[data-cart-tab="cart"]'));
      }
      return;
    }

    const dialogCloser = event.target.closest('[data-dialog-close]');
    if (dialogCloser) {
      const dialog = dialogCloser.closest('dialog');
      if (dialog) {
        event.preventDefault();
        closeDialog(dialog);
      }
      return;
    }

    const detailsCloser = event.target.closest('[data-close-details]');
    if (detailsCloser) {
      const details = detailsCloser.closest('details');
      if (details) {
        event.preventDefault();
        const closeMenu = mobileMenuClosers.get(details);
        if (closeMenu) closeMenu(true);
        else {
          details.removeAttribute('open');
          details.querySelector(':scope > summary')?.focus();
        }
      }
      return;
    }

    const drawer = event.target.closest('[data-cart-drawer]');
    if (drawer) {
      const tab = event.target.closest('[data-cart-tab]');
      if (tab) {
        event.preventDefault();
        const target = tab.dataset.cartTab;
        drawer.querySelectorAll('[data-cart-tab]').forEach((button) => {
          const active = button === tab;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-selected', String(active));
          button.tabIndex = active ? 0 : -1;
        });
        drawer.querySelectorAll('[data-cart-panel]').forEach((panel) => {
          const active = panel.dataset.cartPanel === target;
          panel.classList.toggle('is-active', active);
          panel.hidden = !active;
        });
        drawer.querySelector(`[data-cart-panel="${target}"]`)?.focus({ preventScroll: true });
        return;
      }

      const tool = event.target.closest('[data-cart-tool]');
      if (tool) {
        event.preventDefault();
        const panel = drawer.querySelector(`[data-cart-tool-panel="${tool.dataset.cartTool}"]`);
        tool.setAttribute('aria-expanded', 'true');
        openDialog(panel, tool, panel?.querySelector('[data-dialog-close]'));
        return;
      }

      const noteApply = event.target.closest('[data-cart-note-apply]');
      if (noteApply) {
        event.preventDefault();
        noteApply.disabled = true;
        const note = drawer.querySelector('[data-cart-note]')?.value || '';
        cart.updateNote(note).catch(() => undefined).finally(() => { noteApply.disabled = false; });
        return;
      }

      const recommendationControl = event.target.closest('[data-cart-recommendation-prev], [data-cart-recommendation-next]');
      if (recommendationControl) {
        event.preventDefault();
        const track = drawer.querySelector('[data-cart-recommendation-track]');
        const direction = recommendationControl.matches('[data-cart-recommendation-prev]') ? -1 : 1;
        track?.scrollBy({ left: direction * track.clientWidth, behavior: reduceMotion.matches ? 'instant' : 'smooth' });
        return;
      }

      const recommendationOptions = event.target.closest('[data-cart-recommendation-options]');
      if (recommendationOptions) {
        event.preventDefault();
        const template = drawer.querySelector(`[data-cart-recommendation-template="${recommendationOptions.dataset.cartRecommendationOptions}"]`);
        const panel = drawer.querySelector('[data-cart-tool-panel="options"]');
        panel.querySelector('[data-cart-options-content]').replaceChildren(template.content.cloneNode(true));
        openDialog(panel, recommendationOptions, panel.querySelector('[data-dialog-close]'));
        return;
      }

      const recommendationAdd = event.target.closest('[data-cart-recommendation-add]');
      if (recommendationAdd) {
        event.preventDefault();
        const panel = recommendationAdd.closest('[data-cart-tool-panel]');
        const variant = panel.querySelector('[data-cart-recommendation-variant]')?.value;
        if (!variant) return;
        recommendationAdd.disabled = true;
        cart.enqueue(async () => {
          const response = await fetch(route('cartAdd', '/cart/add.js'), {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(variant), quantity: 1 }], sections: [cart.sectionName()], sections_url: location.pathname })
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || data.message || strings.cartError);
          await cart.applyResponse(data, true);
        }).catch((error) => { panel.querySelector('[data-cart-recommendation-result]').textContent = error.message; })
          .finally(() => { recommendationAdd.disabled = false; });
        return;
      }

      const shippingSubmit = event.target.closest('[data-cart-shipping-submit]');
      if (shippingSubmit) {
        event.preventDefault();
        const province = drawer.querySelector('[data-cart-shipping-province]')?.value.trim() || '';
        const zip = drawer.querySelector('[data-cart-shipping-zip]')?.value.trim() || '';
        const result = drawer.querySelector('[data-cart-shipping-result]');
        if (!province) {
          if (result) {
            result.classList.remove('is-success');
            result.classList.add('is-error');
            result.textContent = result.dataset.selectProvince || 'Please select a province.';
          }
          drawer.querySelector('[data-cart-shipping-province]')?.focus();
          return;
        }
        shippingSubmit.disabled = true;
        if (result) {
          result.classList.remove('is-error', 'is-success');
          result.textContent = strings.loading || 'Calculating shipping…';
        }
        const url = new URL(`${route('root', '/')}cart/shipping_rates.json`, location.origin);
        url.searchParams.set('shipping_address[country]', 'Pakistan');
        url.searchParams.set('shipping_address[province]', province);
        if (zip) url.searchParams.set('shipping_address[zip]', zip);
        fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(async (response) => {
            const data = await response.json();
            const errorMessage =
              typeof data?.error === 'string'
                ? data.error
                : data?.error && typeof data.error === 'object'
                  ? Object.values(data.error).flat().join(' ')
                  : data?.message;
            if (!response.ok) {
              throw new Error(
                errorMessage ||
                  result?.dataset.errorMessage ||
                  'Shipping rates are unavailable.'
              );
            }
            const rates = Array.isArray(data.shipping_rates) ? data.shipping_rates : [];
            if (!result) return;
            result.classList.remove('is-error');
            result.classList.add('is-success');
            result.textContent = rates.length
              ? rates.map((rate) => `${rate.name}: ${rate.price} ${rate.currency || 'PKR'}`).join(' · ')
              : result.dataset.emptyMessage || 'No shipping rates found for this address.';
          })
          .catch((error) => {
            if (result) {
              result.classList.remove('is-success');
              result.classList.add('is-error');
              result.textContent =
                error.message ||
                result.dataset.errorMessage ||
                'Shipping rates are unavailable.';
            }
          })
          .finally(() => {
            shippingSubmit.disabled = false;
          });
        return;
      }

      const discountApply = event.target.closest('[data-cart-discount-apply]');
      if (discountApply) {
        event.preventDefault();
        const input = drawer.querySelector('[data-cart-discount-code]');
        const result = drawer.querySelector('[data-cart-discount-result]');
        const code = input?.value.trim();
        if (!code) {
          if (result) {
            result.classList.remove('is-success');
            result.classList.add('is-error');
            result.textContent = result.dataset.requiredMessage || 'Enter a discount code.';
          }
          input?.focus();
          return;
        }
        discountApply.disabled = true;
        if (result) {
          result.classList.remove('is-error', 'is-success');
          result.textContent = strings.loading || 'Applying discount…';
        }
        cart
          .applyDiscount(code)
          .then(({ applied }) => {
            const currentDrawer = cart.drawer();
            const currentResult = currentDrawer?.querySelector('[data-cart-discount-result]');
            if (!applied) {
              if (currentResult) {
                currentResult.classList.remove('is-success');
                currentResult.classList.add('is-error');
                currentResult.textContent =
                  currentResult.dataset.invalidMessage ||
                  'Discount code cannot be applied to your cart.';
              }
              return;
            }
            const panel = currentDrawer?.querySelector('[data-cart-tool-panel="discount"]');
            const trigger = currentDrawer?.querySelector('[data-cart-tool="discount"]');
            trigger?.setAttribute('aria-expanded', 'true');
            openDialog(panel, trigger, panel?.querySelector('[data-dialog-close]'));
            if (currentResult) {
              currentResult.classList.remove('is-error');
              currentResult.classList.add('is-success');
              currentResult.textContent =
                currentResult.dataset.successMessage || 'Discount code applied.';
            }
          })
          .catch((error) => {
            if (result?.isConnected) {
              result.classList.remove('is-success');
              result.classList.add('is-error');
              result.textContent =
                error.message ||
                result.dataset.errorMessage ||
                'Unable to apply the discount code.';
            }
          })
          .finally(() => {
            if (discountApply.isConnected) discountApply.disabled = false;
          });
        return;
      }

      const decrease = event.target.closest('[data-cart-quantity-decrease]');
      const increase = event.target.closest('[data-cart-quantity-increase]');
      if (decrease || increase) {
        event.preventDefault();
        const item = event.target.closest('[data-cart-item]');
        const input = item?.querySelector('[data-cart-quantity]');
        if (!input) return;
        const next = Math.max(0, Number(input.value || 0) + (increase ? 1 : -1));
        input.value = String(next);
        cart.change(input.dataset.line || item.dataset.line, next).catch(() => undefined);
        return;
      }
      const remove = event.target.closest('[data-cart-remove]');
      if (remove) {
        event.preventDefault();
        cart.change(remove.dataset.line || remove.closest('[data-cart-item]')?.dataset.line, 0).catch(() => undefined);
        return;
      }
    }

    const minus = event.target.closest('[data-qty-minus]');
    const plus = event.target.closest('[data-qty-plus]');
    if (minus || plus) {
      event.preventDefault();
      const selector = event.target.closest('[data-quantity-selector], .quantity');
      const input = selector?.querySelector('[data-quantity-input], [data-cart-quantity], input[type="number"]');
      setQuantity(input, plus ? 1 : -1);
    }
  });

  document.addEventListener('change', (event) => {
    const quantity = event.target.closest('[data-cart-drawer] [data-cart-quantity]');
    if (quantity) {
      const line = quantity.dataset.line || quantity.closest('[data-cart-item]')?.dataset.line;
      cart.change(line, Math.max(0, Number(quantity.value) || 0)).catch(() => undefined);
      return;
    }

  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-cart-drawer] [data-cart-form]');
    if (!form) return;
    if (event.submitter?.name === 'checkout') return;
    event.preventDefault();
    cart.submit(form).catch(() => undefined);
  });

  document.addEventListener('pointerdown', (event) => {
    document.querySelectorAll('[data-nav-disclosure][open]').forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const openDisclosure = document.querySelector('[data-nav-disclosure][open]');
    if (openDisclosure) {
      openDisclosure.removeAttribute('open');
      openDisclosure.querySelector(':scope > summary')?.focus();
    }
  });

  document.addEventListener('shopify:section:load', (event) => initTree(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    elements(event.target, 'dialog, [data-mobile-menu], [data-collection-facets]').forEach((element) => {
      element._whiteAppleSheetCleanup?.();
      element._cartRecommendationResize?.disconnect();
    });
    elements(event.target, '[data-announcement-bar], [data-product-section]').forEach((element) => {
      element._whiteAppleRuntime?.destroy?.();
    });
    elements(event.target, '[data-featured-carousel-section]').forEach((section) => {
      section._whiteAppleCarouselCleanup?.();
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initTree(), { once: true });
  } else {
    initTree();
  }
})();

// Normalize the visible product, not the surrounding whitespace in the source file.
// This is shared by every brand, card gallery, and color thumbnail.
(() => {
  const selector = '.product-card img.product-card__image, .product-card__swatch--image img, .cart-drawer__recent-media img, .cart-drawer__recommendation-media img';
  const revealWithoutFit = !document.documentElement.hasAttribute('data-product-image-fit');
  if (!revealWithoutFit) document.documentElement.dataset.productImageFit = 'active';
  const boundsCache = new Map();
  const fitted = new WeakMap();

  const findProductBounds = (pixels, width, height, cartThumbnail = false) => {
    const sample = (x, y) => {
      const i = (y * width + x) * 4;
      const alpha = pixels[i + 3] / 255;
      return [0, 1, 2].map(channel => pixels[i + channel] * alpha + 255 * (1 - alpha));
    };
    const edges = [];
    for (let x = 0; x < width; x += 3) edges.push(sample(x, 0), sample(x, height - 1));
    for (let y = 0; y < height; y += 3) edges.push(sample(0, y), sample(width - 1, y));
    const background = [0, 1, 2].map(channel => edges.map(p => p[channel]).sort((a, b) => a - b)[Math.floor(edges.length / 2)]);
    const distance = p => Math.max(...p.map((value, channel) => Math.abs(value - background[channel])));
    // Full-bleed/lifestyle photography keeps its original framing.
    if (Math.min(...background) < 215 || Math.max(...background) - Math.min(...background) > 24 || edges.filter(p => distance(p) < 24).length < edges.length * 0.75) return null;
    const mask = new Uint8Array(width * height);
    for (let at = 0; at < mask.length; at++) {
      const offset = at * 4;
      const alpha = pixels[offset + 3] / 255;
      const matte = 255 * (1 - alpha);
      if (Math.max(Math.abs(pixels[offset] * alpha + matte - background[0]), Math.abs(pixels[offset + 1] * alpha + matte - background[1]), Math.abs(pixels[offset + 2] * alpha + matte - background[2])) > (cartThumbnail ? 10 : 24)) mask[at] = 1;
    }
    const components = [];
    const queue = new Int32Array(width * height);
    for (let start = 0; start < mask.length; start++) {
      if (!mask[start]) continue;
      let read = 0;
      let end = 1;
      queue[0] = start;
      mask[start] = 0;
      const box = { left: width, top: height, right: 0, bottom: 0, area: 0 };
      while (read < end) {
        const at = queue[read++];
        const x = at % width;
        const y = Math.floor(at / width);
        box.left = Math.min(box.left, x);
        box.right = Math.max(box.right, x);
        box.top = Math.min(box.top, y);
        box.bottom = Math.max(box.bottom, y);
        box.area++;
        for (const next of [x ? at - 1 : -1, x < width - 1 ? at + 1 : -1, y ? at - width : -1, y < height - 1 ? at + width : -1]) {
          if (next >= 0 && mask[next]) { mask[next] = 0; queue[end++] = next; }
        }
      }
      components.push(box);
    }
    const largest = Math.max(0, ...components.map(box => box.area));
    if (largest < width * height * 0.015) return null;
    const parts = components.filter(box => box.area >= (cartThumbnail ? 3 : Math.max(8, largest * 0.025)));
    const left = Math.max(0, Math.min(...parts.map(box => box.left)) - 1);
    const top = Math.max(0, Math.min(...parts.map(box => box.top)) - 1);
    const right = Math.min(width, Math.max(...parts.map(box => box.right)) + 2);
    const bottom = Math.min(height, Math.max(...parts.map(box => box.bottom)) + 2);
    return { x: (left + right) / (2 * width), y: (top + bottom) / (2 * height), width: (right - left) / width, height: (bottom - top) / height };
  };

  const readBounds = (image, cartThumbnail) => {
    const ratio = image.naturalWidth / image.naturalHeight;
    const width = Math.round(160 * Math.min(1, ratio));
    const height = Math.round(160 * Math.min(1, 1 / ratio));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    context.drawImage(image, 0, 0, width, height);
    return findProductBounds(context.getImageData(0, 0, width, height).data, width, height, cartThumbnail);
  };

  const whenIdle = callback => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(callback, { timeout: 750 });
    else window.setTimeout(callback, 32);
  };

  const applyFit = (image, size) => {
    const bounds = fitted.get(image);
    const width = size?.width ?? image.clientWidth;
    const height = size?.height ?? image.clientHeight;
    if (!bounds || !width || !height) return;
    const contain = Math.min(width / image.naturalWidth, height / image.naturalHeight);
    const drawnWidth = image.naturalWidth * contain;
    const drawnHeight = image.naturalHeight * contain;
    const fill = image.closest('.product-card__swatch--image') ? 0.9 : image.closest('.cart-drawer__recent-media, .cart-drawer__recommendation-media') ? 0.96 : 1;
    const scale = Math.min(2.8, width * fill / (drawnWidth * bounds.width), height * fill / (drawnHeight * bounds.height));
    image.style.setProperty('--product-fit-scale', scale.toFixed(4));
    image.style.setProperty('--product-fit-x', `${((0.5 - bounds.x) * drawnWidth * scale / width * 100).toFixed(3)}%`);
    image.style.setProperty('--product-fit-y', `${((0.5 - bounds.y) * drawnHeight * scale / height * 100).toFixed(3)}%`);
    image.dataset.productImageFit = 'normalized';
  };
  const resize = new ResizeObserver(entries => {
    const measured = entries.map(({ target }) => ({ image: target, width: target.clientWidth, height: target.clientHeight }));
    measured.forEach(({ image, width, height }) => applyFit(image, { width, height }));
  });
  const fit = async image => {
    if (!image.matches?.(selector) || !image.complete || !image.naturalWidth) return;
    if (revealWithoutFit && image.classList.contains('product-card__image')) {
      image.dataset.productImageFit = 'original';
      return;
    }
    const source = image.currentSrc || image.src;
    const cartThumbnail = !!image.closest('.cart-drawer__recent-media, .cart-drawer__recommendation-media');
    const key = `${cartThumbnail ? 'cart:' : ''}${source.replace(/([?&])width=\d+(&|$)/, '$1')}`;
    if (!boundsCache.has(key)) {
      const result = new Promise(resolve => {
        const timeout = window.setTimeout(() => resolve(null), 2000);
        const finish = bounds => { window.clearTimeout(timeout); resolve(bounds); };
        whenIdle(() => {
          try { finish(readBounds(image, cartThumbnail)); }
          catch {
            // Shopify CDN images from another origin require an anonymous CORS sample.
            const sample = new Image();
            sample.crossOrigin = 'anonymous';
            sample.onload = () => whenIdle(() => { try { finish(readBounds(sample, cartThumbnail)); } catch { finish(null); } });
            sample.onerror = () => finish(null);
            const url = new URL(source, location.href);
            url.searchParams.set('width', '160');
            sample.src = url.href;
          }
        });
      });
      boundsCache.set(key, result);
    }
    const bounds = await boundsCache.get(key);
    if (!image.isConnected || (image.currentSrc || image.src) !== source) return;
    fitted.set(image, bounds);
    image.style.removeProperty('--product-fit-scale');
    image.style.removeProperty('--product-fit-x');
    image.style.removeProperty('--product-fit-y');
    if (bounds) { applyFit(image); resize.observe(image); }
    else { image.dataset.productImageFit = 'original'; resize.unobserve(image); }
  };
  window.WhiteAppleTheme.fitProductImage = fit;
  document.addEventListener('load', event => fit(event.target), true);
  const scan = root => root.querySelectorAll(selector).forEach(fit);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll(selector).forEach(image => resize.unobserve(image)));
})();
